import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import HelpButton from "../components/HelpButton";
import HelpFieldIcon from "../components/HelpFieldIcon";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Receipt, FileText, FileArrowDown, Plus, PencilSimple, Trash, ArrowRight, Money, X, Check, ChartLine, Printer, Buildings, Users, Clock, PaperPlaneTilt, Warning, Package, Tag, Gauge, Palette, ArrowCounterClockwise, Envelope, WhatsappLogo, Link as LinkIcon, Copy, MagnifyingGlass } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { CHART_TOOLTIP_STYLE, CHART_TICK_XS } from "../config/uiHelpers";
import { RichTextEditor } from "../components/RichTextEditor";
import { safeHtmlProps } from "../utils/sanitizeHtml";
import { TemplatesTab } from "./BillingTemplates";
import WhatsAppSendModal from "../components/WhatsAppSendModal";
import { hasPerm } from "../utils/permissions";
import ProductPicker from "../components/billing/ProductPicker";
import CustomerSearchPicker from "../components/billing/CustomerSearchPicker";
import OpenInTabLink from "../components/common/OpenInTabLink";
// Phase 7 — additive billing flow polish components
import BillingFlowProgress from "../components/BillingFlowProgress";
import BillingNextStepHint from "../components/BillingNextStepHint";
import BillingAuditTimeline from "../components/BillingAuditTimeline";

const TABS = [
  { key: "estimations", label: "Quotations", icon: FileText, docType: "estimation" },
  { key: "proformas", label: "Proforma", icon: FileArrowDown, docType: "proforma" },
  { key: "invoices", label: "Invoices", icon: Receipt, docType: "invoice" },
  { key: "sales_orders", label: "Sales Orders", icon: Package, docType: null },
  { key: "customers", label: "Customers", icon: Users, docType: null },
  { key: "aging", label: "Aging Report", icon: Clock, docType: null },
  { key: "price_books", label: "Price Books", icon: Tag, docType: null },
  { key: "advanced_reports", label: "Advanced Reports", icon: Gauge, docType: null },
  { key: "collections", label: "Collections", icon: ChartLine, docType: null },
  { key: "templates", label: "Templates", icon: Palette, docType: null },
  { key: "settings", label: "Company Settings", icon: Buildings, docType: null },
];

const fmtInr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN')}`;

const emptyDoc = (user) => ({
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  customer_address: "",
  customer_gstin: "",
  customer_id: "",
  gst_rate: 18,
  discount_pct: 0,
  line_items: [{ row_id: Date.now().toString(36), description: "", quantity: 1, unit_price: 0, discount: 0, hsn_code: "", product_id: "" }],
  notes: "",
  notes_html: "",
  terms: "",
  terms_html: "",
  footer_html: "",
  header_html: "",
  template_id: "",
  letterhead_data_url: "",
  letterhead_opacity: 0.08,
  valid_until: "",
  due_date: "",
  // Zoho-style fields
  place_of_supply: "",
  gst_treatment: "registered_business",
  salesperson: user?.name || "",
  reference_no: "",
  expiry_date: "",
  subject: "",
  created_by: user?.user_id || "",
  created_by_name: user?.name || ""
});

// Indian States & UT with GST codes (used for Place of Supply dropdown)
const INDIAN_STATES = [
  ["01", "Jammu & Kashmir"], ["02", "Himachal Pradesh"], ["03", "Punjab"], ["04", "Chandigarh"],
  ["05", "Uttarakhand"], ["06", "Haryana"], ["07", "Delhi"], ["08", "Rajasthan"], ["09", "Uttar Pradesh"],
  ["10", "Bihar"], ["11", "Sikkim"], ["12", "Arunachal Pradesh"], ["13", "Nagaland"], ["14", "Manipur"],
  ["15", "Mizoram"], ["16", "Tripura"], ["17", "Meghalaya"], ["18", "Assam"], ["19", "West Bengal"],
  ["20", "Jharkhand"], ["21", "Odisha"], ["22", "Chhattisgarh"], ["23", "Madhya Pradesh"], ["24", "Gujarat"],
  ["25", "Daman & Diu"], ["26", "Dadra & Nagar Haveli"], ["27", "Maharashtra"], ["28", "Andhra Pradesh (Old)"],
  ["29", "Karnataka"], ["30", "Goa"], ["31", "Lakshadweep"], ["32", "Kerala"], ["33", "Tamil Nadu"],
  ["34", "Puducherry"], ["35", "Andaman & Nicobar"], ["36", "Telangana"], ["37", "Andhra Pradesh"],
  ["38", "Ladakh"], ["97", "Other Territory"], ["99", "Overseas"],
];

const GST_TREATMENTS = [
  { value: "registered_business", label: "Registered Business — Regular" },
  { value: "registered_composition", label: "Registered Business — Composition" },
  { value: "unregistered_business", label: "Unregistered Business" },
  { value: "consumer", label: "Consumer" },
  { value: "overseas", label: "Overseas" },
  { value: "sez", label: "SEZ" },
  { value: "deemed_export", label: "Deemed Export" },
];

const BillingCenter = ({ user, logout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("estimations");
  const [docs, setDocs] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // { mode, doc }
  // Phase Hotfix-1 — surface backend save errors instead of crashing on 422 detail arrays
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [viewer, setViewer] = useState(null); // full doc for print view
  const [paymentModal, setPaymentModal] = useState(null);
  const [waModal, setWaModal] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [agingReport, setAgingReport] = useState(null);
  const [customerEditor, setCustomerEditor] = useState(null);
  const [priceBooks, setPriceBooks] = useState([]);
  const [priceBookEditor, setPriceBookEditor] = useState(null);
  const [advReports, setAdvReports] = useState({ type: "quotation_aging", data: null });
  const [whatsNext, setWhatsNext] = useState(null); // { doc_type, doc_id, number }
  const [shareDoc, setShareDoc] = useState(null); // { doc, mode: "email" | "whatsapp" }
  const [salesTeam, setSalesTeam] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]); // all templates for selector
  const [billingReload, setBillingReload] = useState(0); // bumps → DocList refetches

  useEffect(() => {
    // Fetch sales team (admin/manager/bdm/bde) for Salesperson dropdown
    api.get(`/users`).then(r => {
      const team = (r.data || []).filter(u => ["admin", "manager", "bdm", "bde", "marketing_head"].includes(u.role));
      setSalesTeam(team);
    }).catch(() => setSalesTeam([]));
    // Fetch doc templates (summary only — no heavy letterhead data)
    axios.get(`${API}/doc-templates?summary=true`).then(r => setDocTemplates(r.data || [])).catch(() => setDocTemplates([]));
  }, []);

  // Phase B — Lead → Quotation Direct Flow (Invisible Deal)
  // If we landed here from /leads with a quickQuoteSeed, switch to estimations tab and open the editor pre-filled.
  useEffect(() => {
    const seed = location.state?.quickQuoteSeed;
    if (!seed) return;
    setActiveTab("estimations");
    // Merge seed onto a fresh empty doc so all required keys exist.
    const base = emptyDoc(user);
    setEditor({
      mode: "create",
      doc: { ...base, ...seed },
    });
    // Clear the state so a refresh / back-nav doesn't re-trigger
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Path A — Sidebar deep-linking via /billing?tab=<tabKey>.
  // Consumes the URL query param ONCE on mount and on subsequent search changes.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (!tab) return;
    // Map URL query params (hyphenated) to internal tab keys (underscored)
    const tabMapping = {
      "estimations": "estimations",
      "proforma": "proformas",
      "invoices": "invoices",
      "sales-orders": "sales_orders",
      "payments": "collections",
      "aging": "aging",
      "price-books": "price_books",
      "customers": "customers",
      "advanced-reports": "advanced_reports",
    };
    const mappedTab = tabMapping[tab];
    if (mappedTab) {
      setActiveTab(mappedTab);
    }
  }, [location.search]);

  useEffect(() => {
    axios.get(`${API}/company-settings`).then(r => setCompanySettings(r.data)).catch(() => { /* ignore */ });
    // Prefetch customers master so the Link-to-Customer dropdown works on every tab
    api.get(`${API}/customers-master?requester_role=${user?.role || ""}&requester_id=${user?.user_id || ""}`)
      .then(r => setCustomers(r.data)).catch(() => { /* ignore */ });
  }, [user?.role, user?.user_id]);

  // Phase B.3 — narrow legacy gates with hasPerm (additive; never widens).
  const canRecordPayment = ["admin", "cfo"].includes(user?.role) && hasPerm(user, "billing.record_payment");
  const canCreate = ["admin", "manager", "bdm", "bde", "cfo"].includes(user?.role)
    && (hasPerm(user, "quotations.create") || hasPerm(user, "billing.create_invoice"));
  const canApproveBdm = ["admin", "manager", "bdm"].includes(user?.role) && hasPerm(user, "quotations.approve_discount");
  const canApproveMgr = ["admin", "manager"].includes(user?.role) && hasPerm(user, "quotations.approve_discount");
  const canSendQuote = hasPerm(user, "quotations.send");
  const canDeleteDoc = hasPerm(user, "quotations.delete") || hasPerm(user, "billing.delete_invoice");
  const canEditDoc = hasPerm(user, "quotations.edit") || hasPerm(user, "billing.edit_invoice");

  const fetchAll = useCallback(async () => {
    const tab = TABS.find(t => t.key === activeTab);
    if (tab?.docType) {
      // Doc-type tabs (Quotations/Proforma/Invoices) are server-paginated and
      // self-managed by DocList; just signal it to (re)load.
      setLoading(false);
      setBillingReload(x => x + 1);
      return;
    }
    setLoading(true);
    try {
      if (activeTab === "collections") {
        const d = new Date();
        const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const r = await axios.get(`${API}/billing-reports/collection?month=${month}`);
        setReport(r.data);
      } else if (activeTab === "customers") {
        const r = await api.get(`${API}/customers-master?requester_role=${user?.role || ""}&requester_id=${user?.user_id || ""}`);
        setCustomers(r.data);
      } else if (activeTab === "aging") {
        const r = await axios.get(`${API}/billing-reports/aging`);
        setAgingReport(r.data);
      } else if (activeTab === "sales_orders") {
        // SalesOrdersTab self-fetches (server-side paginated); nothing to prefetch here.
      } else if (activeTab === "price_books") {
        const r = await axios.get(`${API}/price-books`);
        setPriceBooks(r.data);
      } else if (activeTab === "advanced_reports") {
        const map = { quotation_aging: "quotation-aging", win_loss: "win-loss", bdm_turnaround: "bdm-turnaround", discount_deviation: "discount-deviation" };
        const path = map[advReports.type] || "quotation-aging";
        const r = await axios.get(`${API}/billing-reports/${path}`);
        setAdvReports(a => ({ ...a, data: r.data }));
      }
    } catch (err) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [activeTab, user?.role, user?.user_id, advReports.type]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = async () => {
    const tab = TABS.find(t => t.key === activeTab);
    const base = emptyDoc(user);
    if (tab?.docType) {
      try {
        const r = await axios.get(`${API}/doc-templates/default/${tab.docType}`);
        const t = r.data || {};
        // Compute expiry_date from validity_days
        const today = new Date();
        const expiry = new Date(today.getTime() + (Number(t.validity_days) || 15) * 86400000);
        const expiry_iso = expiry.toISOString().slice(0, 10);
        setEditor({
          mode: "create",
          doc: {
            ...base,
            template_id: t.template_id || "",
            notes_html: t.notes_html || "",
            terms_html: t.terms_html || "",
            footer_html: t.footer_html || "",
            header_html: t.header_html || "",
            letterhead_data_url: t.letterhead_data_url || "",
            letterhead_opacity: t.letterhead_opacity || 0.08,
            subject: t.subject_prefix || "",
            expiry_date: expiry_iso,
            valid_until: expiry_iso,
          },
        });
      } catch {
        // No template — open with empty doc
        setEditor({ mode: "create", doc: base });
      }
    } else {
      setEditor({ mode: "create", doc: base });
    }
  };
  const openEdit = (doc) => setEditor({ mode: "edit", doc: { ...doc, line_items: doc.line_items || [] } });

  // Clone Quotation — frontend-only deep copy → opens Create modal pre-filled.
  // Fresh doc_id/number/status assigned by backend on Save (no API call here).
  const cloneQuote = async (d) => {
    const cloned = { ...d };
    // System / identity fields
    delete cloned.doc_id;
    delete cloned.number;
    delete cloned.status;
    // Approval lifecycle
    delete cloned.approval_status;
    delete cloned.approval_history;
    delete cloned.submitted_at;
    delete cloned.submitted_by;
    delete cloned.approved_at;
    delete cloned.approved_by;
    delete cloned.rejected_at;
    delete cloned.rejected_by;
    delete cloned.reject_reason;
    // Deal linkage
    delete cloned.linked_deal_id;
    delete cloned.linked_deal_title;
    // Conversion / cross-doc links
    delete cloned.converted_to;
    delete cloned.converted_at;
    delete cloned.converted_doc_id;
    delete cloned.invoice_id;
    delete cloned.proforma_id;
    delete cloned.sales_order_id;
    // Price-lock
    delete cloned.price_locked;
    delete cloned.price_lock_at;
    delete cloned.price_lock_by;
    // Payment
    delete cloned.payments;
    delete cloned.payment_history;
    delete cloned.paid_amount;
    delete cloned.payment_status;
    // Public share / engagement telemetry
    delete cloned.share_token;
    delete cloned.public_url;
    delete cloned.public_access_log_count;
    // Communication history
    delete cloned.whatsapp_sends;
    delete cloned.email_sends;
    // Timestamps (backend stamps fresh)
    delete cloned.created_at;
    delete cloned.updated_at;
    // Reset required fields → fresh draft
    cloned.status = "draft";
    cloned.approval_status = "draft";
    // Fresh row_ids on line items to avoid React key collisions
    cloned.line_items = (d.line_items || []).map((item) => ({
      ...item,
      row_id: (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
    }));
    // Recompute validity from default template (same logic as openCreate)
    try {
      const r = await axios.get(`${API}/doc-templates/default/estimation`);
      const t = r.data || {};
      const today = new Date();
      const expiry = new Date(today.getTime() + (Number(t.validity_days) || 15) * 86400000);
      const expiry_iso = expiry.toISOString().slice(0, 10);
      cloned.expiry_date = expiry_iso;
      cloned.valid_until = expiry_iso;
    } catch {
      const today = new Date();
      const expiry = new Date(today.getTime() + 15 * 86400000);
      const expiry_iso = expiry.toISOString().slice(0, 10);
      cloned.expiry_date = expiry_iso;
      cloned.valid_until = expiry_iso;
    }
    setEditor({ mode: "create", doc: cloned });
    toast.success("Quotation cloned — review and save");
  };

  // Phase Hotfix-1 — robust error→string parser for FastAPI 422 (array-of-objects) +
  // 400/403/500 (string detail). PRODUCTION BUG ROOT CAUSE: passing raw 422 detail array
  // to toast/JSX caused React error #31 (objects-as-children) which unmounted the tree
  // and produced the white screen. NEVER render the raw detail object — always stringify.
  const parseApiError = (err, fallback = "Save failed") => {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message ? `${fallback}: ${err.message}` : fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      // Pydantic v2 422 — each entry: { loc:[...], msg, type, input, ctx }
      return detail
        .map((it) => {
          const field = Array.isArray(it.loc) ? it.loc.slice(1).join(".") || it.loc.join(".") : "field";
          const msg = it.msg || "invalid";
          // Strip "Value error, " noise that Pydantic adds for value_error
          return `${field}: ${msg.replace(/^Value error,\s*/, "")}`;
        })
        .join(" · ");
    }
    if (typeof detail === "object") return JSON.stringify(detail);
    return fallback;
  };

  // Light client-side validation so users see field-level errors without round-tripping.
  const validateDocBeforeSave = (doc, docType) => {
    const errs = [];
    if (!doc?.customer_name?.trim()) errs.push("Customer Name");
    // Email is optional — but if entered must be valid (server will 422 otherwise).
    const email = (doc?.customer_email || "").trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.push("Customer Email (invalid format)");
    if (!Array.isArray(doc?.line_items) || doc.line_items.length === 0) {
      errs.push("At least one line item");
    } else {
      doc.line_items.forEach((li, idx) => {
        if (!li?.description?.trim()) errs.push(`Item ${idx + 1}: Description`);
        const qty = Number(li?.quantity ?? li?.qty ?? 0);
        if (!(qty > 0)) errs.push(`Item ${idx + 1}: Qty must be > 0`);
        const rate = Number(li?.rate ?? li?.unit_price ?? 0);
        if (!(rate > 0)) errs.push(`Item ${idx + 1}: Rate must be > 0`);
      });
    }
    return errs;
  };

  const save = async () => {
    const tab = TABS.find(t => t.key === activeTab);
    if (!tab.docType) return;
    setSaveError(""); // reset
    // Client-side validation FIRST
    const clientErrs = validateDocBeforeSave(editor.doc, tab.docType);
    if (clientErrs.length) {
      setSaveError(`Please fill required field(s): ${clientErrs.join(" · ")}`);
      return;
    }
    setSaving(true);
    try {
      // Omit empty customer_email so backend EmailStr validation (which rejects "")
      // passes — an unset optional email is valid, an empty string is not.
      const payload = { ...editor.doc };
      if (!(payload.customer_email || "").trim()) delete payload.customer_email;
      let resp;
      if (editor.mode === "create") {
        resp = await api.post(`/billing/${tab.docType}`, payload);
        toast.success(`${tab.docType} created`);
      } else {
        // Defensive guard — if we are in edit mode but the doc_id is missing,
        // ABORT instead of silently POSTing a new copy. Prevents accidental duplicates.
        if (!editor.doc?.doc_id) {
          const msg = "Edit aborted — missing document ID. Refresh the list and try again.";
          setSaveError(msg);
          toast.error(msg);
          setSaving(false);
          return;
        }
        resp = await api.put(`/billing/${editor.doc.doc_id}`, payload);
        toast.success("Updated");
      }
      const savedId = resp?.data?.doc_id || editor.doc.doc_id;
      const savedNum = resp?.data?.number || editor.doc.number;
      // Phase B — if this estimation originated from a lead, link it back so the lead's
      // status auto-advances to "quotation_sent" and the quotation_id is appended.
      const leadId = editor.doc.source_lead_id;
      if (editor.mode === "create" && tab.docType === "estimation" && leadId && savedId) {
        try {
          const params = new URLSearchParams({
            quotation_id: savedId,
            quotation_number: savedNum || "",
            requester_role: user?.role || "",
            requester_user_id: user?.user_id || "",
            requester_name: user?.name || "",
          });
          await api.post(`${API}/leads/${leadId}/link-quotation?${params.toString()}`);
        } catch { /* non-fatal — quotation already saved */ }
      }
      setEditor(null);
      await fetchAll();
      // Show "What's Next" banner for newly created estimations/proformas/invoices
      if (editor.mode === "create" && tab.docType) {
        setWhatsNext({ doc_type: tab.docType, doc_id: savedId, number: savedNum });
      }
    } catch (err) {
      const friendly = parseApiError(err, "Save failed");
      setSaveError(friendly);
      toast.error(friendly);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete ${doc.number}?`)) return;
    try {
      await api.delete(`/billing/${doc.doc_id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (err) { toast.error(parseApiError(err, "Delete failed")); }
  };

  const convert = async (doc, target) => {
    if (!window.confirm(`Convert ${doc.number} to ${target}?`)) return;
    try {
      await api.post(`/billing/${doc.doc_id}/convert?target_type=${target}`);
      toast.success(`Converted to ${target}`);
      fetchAll();
    } catch (err) {
      const msg = parseApiError(err, "Convert failed");
      if (msg.includes("Credit limit breach") && window.confirm(`${msg}\n\nProceed with manager override?`)) {
        try {
          await api.post(`/billing/${doc.doc_id}/convert?target_type=${target}&credit_override=true`);
          toast.success(`Converted to ${target} (credit override)`);
          fetchAll();
        } catch (err2) { toast.error(parseApiError(err2, "Convert failed")); }
      } else { toast.error(msg); }
    }
  };

  const submitApproval = async (doc) => {
    try {
      const r = await api.post(`/billing/${doc.doc_id}/submit`, {
        by_user_id: user.user_id, by_user_name: user.name, by_role: user.role,
      });
      toast.success(`Submitted — ${r.data.status.replace(/_/g, " ")}`);
      fetchAll();
    } catch (err) { toast.error(parseApiError(err, "Submit failed")); }
  };

  const approveDoc = async (doc) => {
    try {
      const r = await api.post(`/billing/${doc.doc_id}/approve`, {
        by_user_id: user.user_id, by_user_name: user.name, by_role: user.role,
      });
      toast.success(`Advanced to ${r.data.status.replace(/_/g, " ")}`);
      fetchAll();
    } catch (err) { toast.error(parseApiError(err, "Approve failed")); }
  };

  const rejectDoc = async (doc) => {
    const remarks = window.prompt("Rejection reason:", "");
    if (remarks === null) return;
    try {
      await api.post(`/billing/${doc.doc_id}/reject`, {
        by_user_id: user.user_id, by_user_name: user.name, by_role: user.role, remarks,
      });
      toast.success("Rejected — returned to draft");
      fetchAll();
    } catch (err) { toast.error(parseApiError(err, "Reject failed")); }
  };

  const statusBadge = (s) => {
    const map = {
      draft: "bg-slate-100 text-slate-700 border-slate-200",
      unpaid: "bg-red-50 text-red-700 border-red-200",
      partial: "bg-yellow-50 text-yellow-700 border-yellow-200",
      paid: "bg-green-50 text-green-700 border-green-200",
      converted: "bg-blue-50 text-blue-700 border-blue-200",
    };
    return map[s] || "bg-slate-100 text-slate-600 border-slate-200";
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="page-header-sticky">
          <div className="px-8 pt-5 max-w-6xl">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
              <div>
                <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Billing</h1><HelpButton moduleKey="quotation" /></div>
                <p className="text-base text-slate-700 mt-1">Estimations → Proforma → Invoices → Payments · GST-compliant</p>
              </div>
              {canCreate && ["estimations", "proformas", "invoices"].includes(activeTab) && (
                <button data-testid="billing-create-btn" onClick={openCreate}
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium flex items-center gap-2">
                  <Plus size={18} weight="bold" /> New {TABS.find(t => t.key === activeTab).label.replace(/s$/, "")}
                </button>
              )}
              {activeTab === "customers" && canCreate && (
                <button data-testid="customer-master-create-btn" onClick={() => setCustomerEditor({ mode: "create", data: emptyCustomer(user) })}
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium flex items-center gap-2">
                  <Plus size={18} weight="bold" /> New Customer
                </button>
              )}
              {activeTab === "price_books" && ["admin", "manager"].includes(user?.role) && (
                <button data-testid="price-book-create-btn" onClick={() => setPriceBookEditor({ mode: "create", data: { name: "", customer_type: "retail", active: true, items: [] } })}
                  className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium flex items-center gap-2">
                  <Plus size={18} weight="bold" /> New Price Book
                </button>
              )}
              {activeTab === "sales_orders" && ["admin", "manager", "bdm"].includes(user?.role) && (
                <button data-testid="collection-scan-btn" onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/collection-escalations/scan?requester_role=${user.role}`);
                    toast.success(`Scanned ${r.data.scanned} invoices → ${r.data.bde_tasks + r.data.bdm_tasks + r.data.manager_escalations} escalations`);
                  } catch (err) { toast.error("Scan failed"); }
                }} className="rounded-none bg-amber-600 text-white hover:bg-amber-700 px-4 py-3 text-sm flex items-center gap-2">
                  <Warning size={16} weight="fill" /> Run Collection Scan
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-200 mt-6 overflow-x-auto">
              {TABS.map(t => {
                const TIcon = t.icon;
                return (
                  <button key={t.key} data-testid={`billing-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
                    className={`px-5 py-3 text-sm font-medium transition-colors duration-300 border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap ${activeTab === t.key ? 'border-[#002FA7] text-[#002FA7]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                    <TIcon size={16} weight={activeTab === t.key ? "fill" : "regular"} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-8 pt-6 pb-8 max-w-6xl">
          {loading ? (
            <div className="flex justify-center py-20"><div className="loading-spinner"></div></div>
          ) : activeTab === "collections" ? (
            <CollectionReport data={report} fmtInr={fmtInr} />
          ) : activeTab === "settings" ? (
            <CompanySettingsTab user={user} settings={companySettings} onSaved={(s) => setCompanySettings(s)} />
          ) : activeTab === "customers" ? (
            <CustomersTab reloadSignal={billingReload} user={user} fmtInr={fmtInr}
              onEdit={(c) => setCustomerEditor({ mode: "edit", data: c })}
              onDelete={async (c) => {
                if (!window.confirm(`Delete ${c.customer_code}?`)) return;
                try { await api.delete(`${API}/customers-master/${c.customer_id}?requester_role=${user.role}`); toast.success("Deleted"); setBillingReload(x => x + 1); }
                catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
              }} />
          ) : activeTab === "aging" ? (
            <AgingReport data={agingReport} fmtInr={fmtInr} />
          ) : activeTab === "sales_orders" ? (
            <SalesOrdersTab reloadSignal={billingReload} fmtInr={fmtInr} user={user} onChanged={() => setBillingReload(x => x + 1)} />
          ) : activeTab === "price_books" ? (
            <PriceBooksTab books={priceBooks} fmtInr={fmtInr} user={user}
              onEdit={(pb) => setPriceBookEditor({ mode: "edit", data: pb })}
              onDelete={async (pb) => { if (!window.confirm(`Delete ${pb.name}?`)) return;
                try { await axios.delete(`${API}/price-books/${pb.price_book_id}?requester_role=${user.role}`); toast.success("Deleted"); fetchAll(); }
                catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
              }} />
          ) : activeTab === "advanced_reports" ? (
            <AdvancedReportsTab state={advReports} setState={setAdvReports} fmtInr={fmtInr} />
          ) : activeTab === "templates" ? (
            <TemplatesTab user={user} />
          ) : (
            <DocList docType={TABS.find(t => t.key === activeTab)?.docType} tab={activeTab} reloadSignal={billingReload} statusBadge={statusBadge} canRecordPayment={canRecordPayment}
              user={user} canApproveBdm={canApproveBdm} canApproveMgr={canApproveMgr} canCreate={canCreate}
              canEditDoc={canEditDoc} canDeleteDoc={canDeleteDoc} canSendQuote={canSendQuote}
              onEdit={openEdit} onDelete={remove} onConvert={convert} onView={(d) => setViewer(d)}
              onClone={cloneQuote}
              onRecordPayment={(d) => setPaymentModal(d)}
              onSubmit={submitApproval} onApprove={approveDoc} onReject={rejectDoc}
              onWhatsApp={(d) => {
                const isInvoice = d.doc_type === "invoice";
                const isProforma = d.doc_type === "proforma";
                setWaModal({
                  module: isInvoice ? "invoice" : "quotation",
                  receiver: { name: d.customer_name, phone: d.customer_phone || d.customer_whatsapp, type: "customer" },
                  defaultAction: isInvoice ? "send_invoice" : (isProforma ? "send_quotation" : "send_quotation"),
                  context: {
                    variables: {
                      name: d.customer_name || "",
                      customer_name: d.customer_name || "",
                      doc_number: d.number || "",
                      amount: `₹${Number(d.total || 0).toLocaleString("en-IN")}`,
                      expiry_date: (d.expiry_date || "").slice(0, 10),
                    },
                    related_doc: { type: isInvoice ? "invoice" : "quotation", id: d.doc_id, number: d.number },
                  },
                });
              }}
              fmtInr={fmtInr} />
          )}
        </div>
      </div>

      {waModal && <WhatsAppSendModal open={true} onClose={() => setWaModal(null)} {...waModal} user={user} />}

      {editor && <EditorModal editor={editor} setEditor={setEditor} save={save} saving={saving} saveError={saveError} setSaveError={setSaveError} fmtInr={fmtInr} customers={customers} salesTeam={salesTeam} docType={TABS.find(t => t.key === activeTab)?.docType} templates={docTemplates} user={user} />}
      {viewer && <DocViewer doc={viewer} settings={companySettings} onClose={() => setViewer(null)} fmtInr={fmtInr}
        userRole={user?.role || ""}
        onShareEmail={() => setShareDoc({ doc: viewer, mode: "email" })}
        onShareWhatsApp={() => setShareDoc({ doc: viewer, mode: "whatsapp" })}
        onCopyLink={async () => {
          try {
            const r = await api.post(`/billing/${viewer.doc_id}/share-token`);
            await navigator.clipboard.writeText(r.data.public_url);
            toast.success("Share link copied to clipboard");
          } catch { toast.error("Copy failed"); }
        }} />}
      {whatsNext && <WhatsNextBanner info={whatsNext} onClose={() => setWhatsNext(null)} onView={async () => {
        try {
          const r = await api.get(`/billing/${whatsNext.doc_id}`);
          setViewer(r.data); setWhatsNext(null);
        } catch { toast.error("Unable to load document"); }
      }} onConvert={async (target) => {
        try {
          await api.post(`/billing/${whatsNext.doc_id}/convert?target_type=${target}`);
          toast.success(`Converted to ${target}`);
          setWhatsNext(null); fetchAll();
        } catch (err) { toast.error(err?.response?.data?.detail || "Conversion requires approval"); }
      }} onRecordPayment={async () => {
        try {
          const r = await api.get(`/billing/${whatsNext.doc_id}`);
          setPaymentModal(r.data); setWhatsNext(null);
        } catch { toast.error("Unable to load invoice"); }
      }} onShareEmail={async () => {
        try {
          const r = await api.get(`/billing/${whatsNext.doc_id}`);
          setShareDoc({ doc: r.data, mode: "email" });
        } catch { toast.error("Unable to load doc"); }
      }} onShareWhatsApp={async () => {
        try {
          const r = await api.get(`/billing/${whatsNext.doc_id}`);
          setShareDoc({ doc: r.data, mode: "whatsapp" });
        } catch { toast.error("Unable to load doc"); }
      }} onCopyLink={async () => {
        try {
          const r = await api.post(`/billing/${whatsNext.doc_id}/share-token`);
          await navigator.clipboard.writeText(r.data.public_url);
          toast.success("Share link copied to clipboard");
        } catch { toast.error("Copy failed"); }
      }} user={user} /> }
      {shareDoc && <ShareDocModal doc={shareDoc.doc} mode={shareDoc.mode} onClose={() => setShareDoc(null)} user={user} onSent={fetchAll} />}
      {paymentModal && <PaymentModal invoice={paymentModal} user={user} onClose={() => setPaymentModal(null)} onSaved={fetchAll} fmtInr={fmtInr} />}
      {customerEditor && <CustomerEditorModal editor={customerEditor} setEditor={setCustomerEditor} user={user} onSaved={() => { setCustomerEditor(null); setBillingReload(x => x + 1); }} />}
      {priceBookEditor && <PriceBookEditorModal editor={priceBookEditor} setEditor={setPriceBookEditor} user={user} onSaved={() => { setPriceBookEditor(null); fetchAll(); }} />}
    </div>
  );
};

/* -------- Doc List (server-side pagination · sticky search + header · S.No) -------- */
const BILLING_PAGE_SIZES = [50, 100, 250];
const billingPageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push("…");
  const s = Math.max(2, current - 1), e = Math.min(total - 1, current + 1);
  for (let i = s; i <= e; i++) pages.push(i);
  if (current < total - 2) pages.push("…");
  pages.push(total);
  return pages;
};

const DocList = ({ docType, tab, reloadSignal, statusBadge, canRecordPayment, user, canApproveBdm, canApproveMgr, canCreate, canEditDoc = true, canDeleteDoc = true, canSendQuote = true, onEdit, onDelete, onConvert, onView, onClone, onRecordPayment, onSubmit, onApprove, onReject, onWhatsApp, fmtInr }) => {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [gotoPage, setGotoPage] = useState("");
  const [marketer, setMarketer] = useState("");
  const [marketers, setMarketers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const canFilterMarketer = ["estimations", "proformas", "invoices"].includes(tab) && ["admin", "manager", "md"].includes((user?.role || "").toLowerCase());
  const reqIdRef = useRef(0);
  const firstLoadRef = useRef(true);
  const lastReloadRef = useRef(reloadSignal);
  const searchRef = useRef(null);
  const [pageHeaderH, setPageHeaderH] = useState(0);
  const [searchBarH, setSearchBarH] = useState(0);

  const approvalBadge = (s) => {
    const map = { draft: "bg-slate-100 text-slate-600", pending_bdm: "bg-amber-100 text-amber-700", pending_manager: "bg-orange-100 text-orange-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };
    return map[s] || "bg-slate-100 text-slate-600";
  };

  // Measure the shared page header (Billing title + tabs) so the search bar docks below it.
  useLayoutEffect(() => {
    const el = document.querySelector(".page-header-sticky");
    if (!el) return;
    const upd = () => setPageHeaderH(el.offsetHeight);
    upd();
    const ro = new ResizeObserver(upd); ro.observe(el);
    window.addEventListener("resize", upd);
    return () => { ro.disconnect(); window.removeEventListener("resize", upd); };
  }, []);
  // Measure the sticky search/filter bar so the table header docks below it.
  useLayoutEffect(() => {
    const el = searchRef.current;
    if (!el) return;
    const upd = () => setSearchBarH(el.offsetHeight);
    upd();
    const ro = new ResizeObserver(upd); ro.observe(el);
    return () => ro.disconnect();
  });

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search.trim()), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch, pageSize, docType, marketer]);

  // Marketer options (Admin/Manager only) — reuse existing users; label as Marketer.
  useEffect(() => {
    if (!canFilterMarketer) return;
    api.get("/users").then(r => {
      setMarketers((r.data || []).filter(u => ["bde", "bdm"].includes((u.role || "").toLowerCase())));
    }).catch(() => {});
  }, [canFilterMarketer]);

  // Server-side value summary (Today/Week/Month) — full authorised dataset, filter-aware.
  useEffect(() => {
    const p = new URLSearchParams({ doc_type: docType });
    if (debouncedSearch) p.set("search", debouncedSearch);
    if (marketer) p.set("marketer_id", marketer);
    if (showCustom && customFrom && customTo) { p.set("date_from", customFrom); p.set("date_to", customTo); }
    api.get(`/billing/summary?${p.toString()}`).then(r => setSummary(r.data)).catch(() => setSummary(null));
  }, [docType, debouncedSearch, marketer, reloadSignal, showCustom, customFrom, customTo]);

  const fetchDocs = useCallback(async () => {
    if (!docType) return;
    const rid = ++reqIdRef.current;
    if (firstLoadRef.current) setLoading(true); else setRefreshing(true);
    setError(false);
    try {
      const params = new URLSearchParams({ doc_type: docType, page: String(page), page_size: String(pageSize) });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (marketer) params.set("marketer_id", marketer);
      const r = await api.get(`/billing?${params.toString()}`);
      if (rid !== reqIdRef.current) return;   // ignore stale response
      const d = r.data || {};
      setItems(d.items || []);
      setTotalCount(d.total_count || 0);
      setFilteredCount(d.filtered_count ?? d.total_count ?? 0);
      setTotalPages(d.total_pages || 1);
      if (d.page && d.page !== page) setPage(d.page);
    } catch {
      if (rid !== reqIdRef.current) return;
      setError(true); setItems([]);
    } finally {
      if (rid === reqIdRef.current) { setLoading(false); setRefreshing(false); firstLoadRef.current = false; }
    }
  }, [docType, page, pageSize, debouncedSearch, marketer]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  // External reload after create/edit/delete/clone/convert/approve.
  useEffect(() => {
    if (lastReloadRef.current === reloadSignal) return;
    lastReloadRef.current = reloadSignal;
    fetchDocs();
  }, [reloadSignal, fetchDocs]);

  const goTo = () => { const n = parseInt(gotoPage, 10); if (!Number.isNaN(n) && n >= 1 && n <= totalPages) setPage(n); setGotoPage(""); };
  const start = filteredCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, filteredCount);
  const nf = (n) => (n || 0).toLocaleString("en-IN");
  const label = tab === "estimations" ? "Quotations" : tab.charAt(0).toUpperCase() + tab.slice(1);
  const isEst = tab === "estimations";
  const colCount = isEst ? 8 : 7;
  const fmtInrFull = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtCompact = (n) => {
    const v = Number(n || 0);
    if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
    return `₹${v.toLocaleString("en-IN")}`;
  };
  const KpiCard = ({ title, data }) => (
    <div className="flex-1 min-w-[120px] bg-white border border-slate-200 rounded px-3 py-2" data-testid={`${tab}-kpi-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{title}</div>
      <div className="text-lg font-bold text-[#002FA7] tabular-nums leading-tight" title={fmtInrFull(data?.amount)}>{fmtCompact(data?.amount)}</div>
      <div className="text-[11px] text-slate-500">{nf(data?.count)} Quotes</div>
    </div>
  );

  return (
    <div data-testid={`${tab}-list`}>
      {/* Compact QUOTED-VALUE summary — full authorised dataset, filter-aware */}
      {summary && (
        <div className="flex items-stretch gap-2 mb-3 flex-wrap" data-testid={`${tab}-value-summary`}>
          <div className="flex items-center px-1 text-[11px] uppercase tracking-wider font-bold text-slate-500">Total Quoted Value</div>
          <KpiCard title="Today" data={summary.today} />
          <KpiCard title="This Week" data={summary.week} />
          <KpiCard title="This Month" data={summary.month} />
          <button onClick={() => setShowCustom(s => !s)} data-testid={`${tab}-kpi-custom-toggle`}
            className={`px-3 py-2 border rounded text-xs font-bold ${showCustom ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>Custom</button>
          {showCustom && (
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded px-2 py-1.5">
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} data-testid={`${tab}-custom-from`} className="text-xs border border-slate-300 rounded px-1.5 py-1 outline-none" />
              <span className="text-slate-400 text-xs">→</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} data-testid={`${tab}-custom-to`} className="text-xs border border-slate-300 rounded px-1.5 py-1 outline-none" />
            </div>
          )}
          {showCustom && customFrom && customTo && summary.selected_period && (
            <KpiCard title="Custom Period" data={summary.selected_period} />
          )}
        </div>
      )}
      {/* STICKY search / filters / count bar */}
      <div ref={searchRef} className="sticky z-20 bg-slate-50 border border-slate-200 border-b-0 px-4 py-3 flex items-center gap-3 flex-wrap" style={{ top: pageHeaderH }}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total {label}</div>
          <div className="text-base font-bold text-slate-800 tabular-nums" data-testid={`${tab}-total-count`}>{nf(totalCount)}</div>
        </div>
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={`${tab}-search`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setDebouncedSearch(search.trim()); }}
            placeholder="Search quotations — number, customer, GST, phone, email, status…"
            className="w-full pl-9 pr-8 py-2 border border-slate-300 rounded text-sm outline-none focus:border-[#002FA7] bg-white"
          />
          {search && (
            <button type="button" onClick={() => { setSearch(""); setDebouncedSearch(""); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
              data-testid={`${tab}-search-clear`} aria-label="Clear search"><X size={12} /></button>
          )}
        </div>
        {canFilterMarketer && (
          <select value={marketer} onChange={e => setMarketer(e.target.value)} data-testid={`${tab}-marketer-filter`}
            className="px-2.5 py-2 border border-slate-300 rounded bg-white outline-none focus:border-[#002FA7] text-sm max-w-[170px]">
            <option value="">All Marketers</option>
            {marketers.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </select>
        )}
        <div className="text-xs text-slate-500 tabular-nums ml-auto" data-testid={`${tab}-count`}>
          {filteredCount === 0 ? "0 results"
            : debouncedSearch
              ? <>{nf(filteredCount)} results from {nf(totalCount)}</>
              : <>Showing {nf(start)}–{nf(end)} of {nf(totalCount)}</>}
        </div>
        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} data-testid={`${tab}-page-size`}
          className="px-2 py-1.5 border border-slate-300 rounded bg-white outline-none focus:border-[#002FA7] text-sm">
          {BILLING_PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 shadow-sm relative">
        {refreshing && <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#002FA7]/70 animate-pulse z-30" data-testid={`${tab}-refreshing`} />}
        <table className="w-full table-fixed text-sm" data-testid={`${tab}-table`}>
          <colgroup>
            <col style={{ width: "56px" }} />
            <col style={{ width: "160px" }} />
            <col />
            <col style={{ width: "104px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "104px" }} />
            {isEst && <col style={{ width: "116px" }} />}
            <col style={{ width: "168px" }} />
          </colgroup>
          <thead className="sticky z-10 bg-slate-50 border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.05)]" style={{ top: pageHeaderH + searchBarH }}>
            <tr className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
              <th className="text-left px-4 py-3">S.No.</th>
              <th className="text-left px-4 py-3">Quotation No.</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
              {isEst && <th className="text-left px-4 py-3">Approval</th>}
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className={refreshing ? "opacity-50 transition-opacity" : "transition-opacity"}>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-b border-slate-100">
                  {Array.from({ length: colCount }).map((__, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}
                </tr>
              ))
            ) : error ? (
              <tr><td colSpan={colCount} className="p-12 text-center text-slate-500">Couldn’t load {label.toLowerCase()}. <button onClick={fetchDocs} className="text-[#002FA7] underline" data-testid={`${tab}-retry`}>Retry</button></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={colCount} className="p-16 text-center" data-testid={`${tab}-empty`}>
                <Receipt size={48} weight="duotone" className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">{debouncedSearch ? "No quotations match your search." : `No ${tab} yet`}</p>
              </td></tr>
            ) : items.map((d, idx) => (
              <tr key={d.doc_id} data-testid={`billing-row-${d.doc_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-500 tabular-nums" data-testid={`${tab}-sl-${d.doc_id}`}>{(page - 1) * pageSize + idx + 1}</td>
                <td className="px-4 py-3 font-mono font-bold text-sm">
                  <div className="flex items-center">
                    <a href={`/billing/${d.doc_type || "quotation"}/${d.doc_id}`} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()} data-testid={`billing-doc-link-${d.doc_id}`} className="hover:underline hover:text-[#002FA7] truncate">{d.number}</a>
                    <OpenInTabLink to={`/billing/${d.doc_type || "quotation"}/${d.doc_id}`} testId={`billing-open-tab-${d.doc_id}`} size={11} />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold text-sm truncate">{d.customer_name}</div>
                  {d.customer_gstin && <div className="text-xs text-slate-400 font-mono truncate">{d.customer_gstin}</div>}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">{(d.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="font-bold text-[#002FA7] tabular-nums">{fmtInr(d.total)}</div>
                  {d.status === "partial" && <div className="text-xs text-slate-500">Paid {fmtInr(d.amount_paid || 0)}</div>}
                  {d.discount_pct > 0 && <div className="text-xs text-amber-600">−{d.discount_pct}% disc</div>}
                </td>
                <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 border text-xs font-bold uppercase tracking-wider ${statusBadge(d.status)}`}>{d.status}</span></td>
                {isEst && (
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${approvalBadge(d.approval_status || "draft")}`}>{(d.approval_status || "draft").replace(/_/g, " ")}</span>
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1 flex-wrap">
                    <button onClick={() => onView(d)} data-testid={`view-${d.doc_id}`} className="p-2 hover:bg-slate-100" title="View/Print"><Printer size={16} /></button>
                    {onWhatsApp && (d.customer_phone || d.customer_whatsapp) && canSendQuote && (
                      <button onClick={() => onWhatsApp(d)} data-testid={`wa-${d.doc_id}`} className="p-2 hover:bg-emerald-50 text-emerald-600" title="Send via WhatsApp"><WhatsappLogo size={16} weight="fill" /></button>
                    )}
                    {d.doc_type === "estimation" && (d.approval_status || "draft") === "draft" && (
                      <button onClick={() => onSubmit(d)} data-testid={`submit-${d.doc_id}`} className="p-2 hover:bg-blue-50 text-[#002FA7]" title="Submit for approval"><PaperPlaneTilt size={16} /></button>
                    )}
                    {d.doc_type === "estimation" && d.approval_status === "pending_bdm" && canApproveBdm && d.created_by !== user?.user_id && (
                      <>
                        <button onClick={() => onApprove(d)} data-testid={`approve-${d.doc_id}`} className="p-2 hover:bg-emerald-50 text-emerald-600" title="Approve"><Check size={16} weight="bold" /></button>
                        <button onClick={() => onReject(d)} className="p-2 hover:bg-red-50 text-red-500" title="Reject"><X size={16} weight="bold" /></button>
                      </>
                    )}
                    {d.doc_type === "estimation" && d.approval_status === "pending_manager" && canApproveMgr && d.created_by !== user?.user_id && (
                      <>
                        <button onClick={() => onApprove(d)} data-testid={`approve-${d.doc_id}`} className="p-2 hover:bg-emerald-50 text-emerald-600" title="Approve"><Check size={16} weight="bold" /></button>
                        <button onClick={() => onReject(d)} className="p-2 hover:bg-red-50 text-red-500" title="Reject"><X size={16} weight="bold" /></button>
                      </>
                    )}
                    {d.doc_type === "estimation" && d.status !== "converted" && d.approval_status === "approved" && (
                      <>
                        <button onClick={() => onConvert(d, "proforma")} data-testid={`convert-pi-${d.doc_id}`} className="p-2 hover:bg-blue-50 text-[#002FA7]" title="Convert to Proforma"><ArrowRight size={16} /></button>
                        <button onClick={() => onConvert(d, "invoice")} className="px-2 text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:underline">→ INV</button>
                      </>
                    )}
                    {d.doc_type === "proforma" && d.status !== "converted" && (
                      <button onClick={() => onConvert(d, "invoice")} data-testid={`convert-inv-${d.doc_id}`} className="p-2 hover:bg-blue-50 text-[#002FA7]" title="Convert to Invoice"><ArrowRight size={16} /></button>
                    )}
                    {d.doc_type === "invoice" && canRecordPayment && d.status !== "paid" && (
                      <button onClick={() => onRecordPayment(d)} data-testid={`pay-${d.doc_id}`} className="p-2 hover:bg-green-50 text-green-600" title="Record Payment"><Money size={16} weight="fill" /></button>
                    )}
                    {d.status !== "paid" && d.status !== "converted" && canEditDoc && (
                      <button onClick={() => onEdit(d)} data-testid={`edit-${d.doc_id}`} title="Edit" className="p-2 hover:bg-slate-100"><PencilSimple size={16} /></button>
                    )}
                    {tab === "estimations" && canCreate && onClone && (
                      <button data-testid={`clone-quotation-${d.doc_id}`} onClick={() => onClone(d)} title="Clone Quotation" className="p-2 hover:bg-blue-50 text-[#002FA7]"><Copy size={16} /></button>
                    )}
                    {d.status !== "paid" && canDeleteDoc && (
                      <button onClick={() => onDelete(d)} data-testid={`del-${d.doc_id}`} title="Delete" className="p-2 hover:bg-red-50 text-red-500"><Trash size={16} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredCount > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap mt-3 text-sm" data-testid={`${tab}-pagination`}>
          <div className="text-xs text-slate-500 tabular-nums">Showing {nf(start)}–{nf(end)} of {nf(filteredCount)} {debouncedSearch ? "matching" : label.toLowerCase()}</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid={`${tab}-prev`}
              className="px-2.5 py-1.5 border border-slate-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">‹ Prev</button>
            {billingPageList(page, totalPages).map((n, i) => n === "…" ? (
              <span key={`e${i}`} className="px-1.5 text-slate-400">…</span>
            ) : (
              <button key={n} onClick={() => setPage(n)} data-testid={`${tab}-page-${n}`}
                className={`min-w-[32px] px-2 py-1.5 border rounded ${n === page ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-300 hover:bg-slate-50"}`}>{n}</button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid={`${tab}-next`}
              className="px-2.5 py-1.5 border border-slate-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">Next ›</button>
            {totalPages > 7 && (
              <div className="flex items-center gap-1 ml-1">
                <input value={gotoPage} onChange={e => setGotoPage(e.target.value.replace(/\D/g, ""))} onKeyDown={e => e.key === "Enter" && goTo()}
                  placeholder="Go" data-testid={`${tab}-goto-input`} className="w-14 px-2 py-1.5 border border-slate-300 rounded outline-none focus:border-[#002FA7]" />
                <button onClick={goTo} data-testid={`${tab}-goto-btn`} className="px-2 py-1.5 border border-slate-300 rounded hover:bg-slate-50">Go</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* -------- Editor Modal -------- */
const EditorModal = ({ editor, setEditor, save, saving = false, saveError = "", setSaveError = () => {}, fmtInr, customers = [], salesTeam = [], docType = "estimation", templates = [], user = null }) => {
  const { doc } = editor;
  const update = (patch) => {
    if (saveError) setSaveError(""); // clear stale error banner once user starts editing again
    setEditor(e => ({ ...e, doc: { ...e.doc, ...patch } }));
  };
  const updateItem = (i, patch) => {
    const items = [...doc.line_items];
    items[i] = { ...items[i], ...patch };
    update({ line_items: items });
  };
  const addItem = () => update({ line_items: [...doc.line_items, { row_id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6), description: "", quantity: 1, unit_price: 0, discount: 0, hsn_code: "", product_id: "" }] });
  const rmItem = (i) => update({ line_items: doc.line_items.filter((_, idx) => idx !== i) });

  const [products, setProducts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data || [])).catch(() => setProducts([]));
  }, []);

  const typeTemplates = templates.filter(t => t.doc_type === docType);

  const applyTemplate = async (template_id) => {
    if (!template_id) { update({ template_id: "" }); return; }
    try {
      const r = await axios.get(`${API}/doc-templates/${template_id}`);
      const t = r.data || {};
      const today = new Date();
      const expiry = new Date(today.getTime() + (Number(t.validity_days) || 15) * 86400000);
      const expiry_iso = expiry.toISOString().slice(0, 10);
      update({
        template_id: t.template_id || "",
        notes_html: t.notes_html || "",
        terms_html: t.terms_html || "",
        footer_html: t.footer_html || "",
        header_html: t.header_html || "",
        letterhead_data_url: t.letterhead_data_url || "",
        letterhead_opacity: t.letterhead_opacity || 0.08,
        subject: (t.subject_prefix || "") + (doc.customer_name ? doc.customer_name : ""),
        expiry_date: expiry_iso,
        valid_until: expiry_iso,
      });
      toast.success(`Applied "${t.name}"`);
    } catch { toast.error("Could not load template"); }
  };

  const pickProduct = (i, product_id) => {
    const p = products.find(x => x.product_id === product_id);
    if (!p) { updateItem(i, { product_id: "" }); return; }
    updateItem(i, {
      product_id,
      description: p.name + (p.brand_name ? ` — ${p.brand_name}` : p.brand ? ` — ${p.brand}` : ""),
      unit_price: Number(p.price || p.retail_price || 0),
      hsn_code: p.hsn || p.hsn_sac || "",
      gst_percent: Number(p.gst_percent ?? p.gst ?? 18),
    });
  };

  // Wrapper for ProductPicker — accepts the full product object (or null for "Manual")
  const onPickerSelect = (i, product) => {
    if (!product) {
      // Manual mode — clear product_id but preserve whatever user typed in description
      updateItem(i, { product_id: "" });
      return;
    }
    pickProduct(i, product.product_id);
  };

  // Accepts EITHER a customer_id string (legacy select dropdown) OR a full customer
  // object from the new CustomerSearchPicker. The picker's payload uses the same
  // field names as `customers` so the body below works for both cases.
  const linkCustomer = (idOrObj) => {
    if (!idOrObj) { update({ customer_id: "", customer_name: "", customer_gstin: "", customer_email: "", customer_phone: "" }); return; }
    const c = typeof idOrObj === "string" ? customers.find(x => x.customer_id === idOrObj) : idOrObj;
    if (c) {
      const addr = c.billing_address || {};
      // Auto-derive place_of_supply from customer billing state (if present)
      let pos = doc.place_of_supply;
      if (addr.state && !pos) {
        const match = INDIAN_STATES.find(([, name]) => name.toLowerCase() === String(addr.state).toLowerCase());
        if (match) pos = `${match[0]}-${match[1]}`;
      }
      // Auto-derive GST treatment
      const treatment = c.gstin ? "registered_business" : (doc.gst_treatment || "consumer");
      update({
        customer_id: c.customer_id,
        customer_name: c.legal_name || c.display_name,
        customer_gstin: c.gstin || "",
        customer_email: c.email || "",
        customer_phone: c.phone || "",
        customer_address: [addr.line1, addr.city, addr.state, addr.pincode].filter(Boolean).join(", "),
        place_of_supply: pos,
        gst_treatment: treatment,
      });
    }
  };

  const { subtotal, discount, tax, total } = useMemo(() => {
    let sub = 0;
    doc.line_items.forEach(li => {
      const qty = Number(li.quantity) || 0;
      const unit = Number(li.unit_price) || 0;
      const disc = Number(li.discount) || 0;
      sub += qty * unit * (1 - disc / 100);
    });
    const overallDisc = Number(doc.discount_pct) || 0;
    const discountAmt = sub * overallDisc / 100;
    const afterDisc = sub - discountAmt;
    const t = afterDisc * (Number(doc.gst_rate) || 0) / 100;
    return { subtotal: sub, discount: discountAmt, tax: t, total: afterDisc + t };
  }, [doc.line_items, doc.gst_rate, doc.discount_pct]);

  const docLabel = { estimation: "Quotation", proforma: "Proforma Invoice", invoice: "Invoice" }[docType] || "Document";

  return (
    <div data-testid="billing-editor-overlay" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => { if (!saving) setEditor(null); }}>
      <div data-testid="billing-editor" className="bg-white shadow-2xl max-w-6xl w-full my-4" onClick={e => e.stopPropagation()}>

        {/* Zoho-style sticky header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-4 flex justify-between items-center z-20">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{editor.mode === "create" ? "New" : "Edit"}</div>
            <h2 className="text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{docLabel}</h2>
          </div>
          <div className="flex items-center gap-3">
            {typeTemplates.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Template</label>
                <select data-testid="template-selector" value={doc.template_id || ""} onChange={e => applyTemplate(e.target.value)}
                  className="rounded border border-slate-300 focus:border-[#002FA7] px-3 py-2 text-sm bg-white outline-none">
                  <option value="">— None —</option>
                  {typeTemplates.map(t => <option key={t.template_id} value={t.template_id}>{t.name}{t.is_default ? " ★" : ""}</option>)}
                </select>
                {doc.template_id && (
                  <button type="button" onClick={() => applyTemplate(doc.template_id)} title="Reset content to template"
                    className="p-2 border border-slate-300 hover:bg-slate-50 rounded text-slate-600" data-testid="reset-to-template">
                    <ArrowCounterClockwise size={14} />
                  </button>
                )}
              </div>
            )}
            <button onClick={() => setEditor(null)} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Close"><X size={20} /></button>
          </div>
        </div>

        <div className="p-8 space-y-8">

          {/* SECTION 1 — Customer & GST */}
          <section>
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-6">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Customer Name <span className="text-red-500">*</span></label>
                <div className="mb-2">
                  <CustomerSearchPicker
                    user={user}
                    value={doc.customer_id}
                    selectedLabel={doc.customer_id ? `${doc.customer_name || ""}${doc.customer_gstin ? " · " + doc.customer_gstin : ""}` : ""}
                    onPick={(c) => linkCustomer(c)}
                    onClear={() => linkCustomer(null)}
                  />
                </div>
                <input data-testid="cust-name" placeholder="…or type a new customer name to auto-create" required value={doc.customer_name} onChange={e => update({ customer_name: e.target.value })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm" />
              </div>
              <div className="col-span-12 md:col-span-6">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">GST Treatment</label>
                <select data-testid="gst-treatment" value={doc.gst_treatment || "registered_business"} onChange={e => update({ gst_treatment: e.target.value })}
                  className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm bg-white">
                  {GST_TREATMENTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">Determines tax applicability on this document.</div>
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">GSTIN</label>
                <input data-testid="cust-gstin" placeholder="22AAAAA0000A1Z5" value={doc.customer_gstin} onChange={e => update({ customer_gstin: e.target.value.toUpperCase() })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm font-mono" />
              </div>
              <div className="col-span-12 md:col-span-6">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Place of Supply <span className="text-red-500">*</span></label>
                <select data-testid="place-of-supply" value={doc.place_of_supply || ""} onChange={e => update({ place_of_supply: e.target.value })}
                  className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm bg-white">
                  <option value="">— Select state —</option>
                  {INDIAN_STATES.map(([code, name]) => (
                    <option key={code} value={`${code}-${name}`}>{`[${code}] ${name}`}</option>
                  ))}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">Used for GST jurisdiction (CGST+SGST vs IGST).</div>
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Customer Email</label>
                <input data-testid="cust-email" placeholder="billing@acme.com" value={doc.customer_email} onChange={e => update({ customer_email: e.target.value })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm" />
              </div>
              <div className="col-span-12 md:col-span-6">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Phone</label>
                <input placeholder="+91 98765 43210" value={doc.customer_phone} onChange={e => update({ customer_phone: e.target.value })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm" />
              </div>

              <div className="col-span-12">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Billing Address</label>
                <textarea placeholder="Street · City · State · Pincode" rows={2} value={doc.customer_address} onChange={e => update({ customer_address: e.target.value })} className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2.5 outline-none text-sm" />
              </div>
            </div>
          </section>

          {/* SECTION 2 — Document Meta (Zoho-style) */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3 border-b border-slate-200 pb-2">Document Details</div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-4">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Reference#</label>
                <input data-testid="reference-no" placeholder="Customer PO / Ref no." value={doc.reference_no || ""} onChange={e => update({ reference_no: e.target.value })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm" />
              </div>
              <div className="col-span-12 md:col-span-4">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">{docType === "invoice" ? "Due Date" : docType === "proforma" ? "Valid Until" : "Quote Date"}</label>
                <input type="date" value={doc.valid_until || ""} onChange={e => update({ valid_until: e.target.value })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm" />
              </div>
              <div className="col-span-12 md:col-span-4">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Expiry Date</label>
                <input data-testid="expiry-date" type="date" value={doc.expiry_date || ""} onChange={e => update({ expiry_date: e.target.value })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm" />
                <div className="text-[11px] text-slate-500 mt-1">After this date, the quote auto-expires.</div>
              </div>

              <div className="col-span-12 md:col-span-6">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Salesperson</label>
                <select data-testid="salesperson" value={doc.salesperson || ""} onChange={e => update({ salesperson: e.target.value })}
                  className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm bg-white">
                  <option value="">— Select Salesperson —</option>
                  {salesTeam.map(u => <option key={u.user_id} value={u.name}>{u.name} ({u.role})</option>)}
                </select>
              </div>
              <div className="col-span-12 md:col-span-6">
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Subject</label>
                <input data-testid="doc-subject" placeholder="Let your customer know what this is for" value={doc.subject || ""} onChange={e => update({ subject: e.target.value })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2.5 w-full outline-none text-sm" />
              </div>
            </div>
          </section>

          {/* SECTION 3 — Line Items */}
          <section>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3 border-b border-slate-200 pb-2">Item Table</div>
            <div className="border border-slate-200 rounded overflow-hidden">
              <div className="grid grid-cols-[180px_1fr_80px_60px_100px_70px_110px_40px] gap-2 bg-slate-50 px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600 border-b border-slate-200">
                <div>Product</div><div>Item / Description</div><div>HSN/SAC</div><div className="text-right">Qty</div><div className="text-right">Rate</div><div className="text-right">Disc %</div><div className="text-right">Amount</div><div></div>
              </div>
              {doc.line_items.map((li, i) => {
                const rowKey = li.row_id || `${li.product_id || "new"}-${i}`;
                return (
                  <div key={rowKey} className="grid grid-cols-[180px_1fr_80px_60px_100px_70px_110px_40px] gap-2 items-center px-3 py-2 border-b border-slate-100 hover:bg-slate-50/50">
                    <ProductPicker
                      testId={`li-prod-${i}`}
                      value={li.product_id || ""}
                      products={products}
                      onSelect={(p) => onPickerSelect(i, p)}
                    />
                    <textarea data-testid={`li-desc-${i}`} placeholder="Type or select item — press Enter for new line" value={li.description} onChange={e => updateItem(i, { description: e.target.value })} rows={2} className="rounded border border-slate-200 p-2 text-sm outline-none resize-y leading-snug" />
                    <input data-testid={`li-hsn-${i}`} placeholder="HSN" value={li.hsn_code || ""} onChange={e => updateItem(i, { hsn_code: e.target.value })} className="rounded border border-slate-200 p-2 text-xs font-mono outline-none" />
                    <input data-testid={`li-qty-${i}`} type="number" min="0" step="1" value={li.quantity} onChange={e => updateItem(i, { quantity: e.target.value })} className="rounded border border-slate-200 p-2 text-sm text-right outline-none" />
                    <input data-testid={`li-rate-${i}`} type="number" min="0" step="0.01" value={li.unit_price} onChange={e => updateItem(i, { unit_price: e.target.value })} className="rounded border border-slate-200 p-2 text-sm text-right outline-none" />
                    <input type="number" min="0" max="100" value={li.discount} onChange={e => updateItem(i, { discount: e.target.value })} className="rounded border border-slate-200 p-2 text-sm text-right outline-none" />
                    <div className="text-right text-sm font-semibold">{fmtInr((Number(li.quantity) || 0) * (Number(li.unit_price) || 0) * (1 - (Number(li.discount) || 0) / 100))}</div>
                    <button onClick={() => rmItem(i)} className="p-1 hover:bg-red-50 text-red-500 disabled:opacity-30" disabled={doc.line_items.length === 1}><Trash size={14} /></button>
                  </div>
                );
              })}
            </div>
            <button onClick={addItem} data-testid="add-line-item" className="mt-3 text-sm text-[#002FA7] font-bold hover:underline flex items-center gap-1"><Plus size={14} weight="bold" /> Add another line</button>
          </section>

          {/* SECTION 4 — Summary + Notes + Terms (two columns like Zoho) */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Customer Notes</label>
                <RichTextEditor value={doc.notes_html || ""} onChange={v => update({ notes_html: v })} placeholder="Thanks for your business." testId="doc-notes" minHeight={110} />
                <div className="text-[11px] text-slate-500 mt-1">Will be printed on the document.</div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 block mb-2">Terms &amp; Conditions</label>
                <RichTextEditor value={doc.terms_html || ""} onChange={v => update({ terms_html: v })} placeholder="Payment terms, warranty, delivery…" testId="doc-terms" minHeight={110} />
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-5 self-start">
              <div className="flex justify-between py-1 text-sm text-slate-700"><span>Sub Total</span><span className="font-semibold text-slate-900">{fmtInr(subtotal)}</span></div>

              <div className="grid grid-cols-2 gap-3 items-center mt-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 block mb-1">Overall Discount % <HelpFieldIcon moduleKey="quotation" fieldKey="discount_pct" /></label>
                  <input data-testid="overall-discount" type="number" min="0" max="100" step="0.01" value={doc.discount_pct || 0} onChange={e => update({ discount_pct: Number(e.target.value) })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2 w-full outline-none text-sm bg-white" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 block mb-1">GST Rate % <HelpFieldIcon moduleKey="quotation" fieldKey="gst_type" /></label>
                  <input data-testid="gst-rate" type="number" min="0" step="0.01" value={doc.gst_rate} onChange={e => update({ gst_rate: e.target.value })} className="rounded border border-slate-300 focus:border-[#002FA7] p-2 w-full outline-none text-sm bg-white" />
                </div>
              </div>

              {Number(doc.discount_pct) > 15 && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">⚠ Needs Manager approval (discount &gt; 15%)</div>}
              {Number(doc.discount_pct) > 5 && Number(doc.discount_pct) <= 15 && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">⚠ Needs BDM approval (discount &gt; 5%)</div>}

              {discount > 0 && <div className="flex justify-between py-1 text-sm text-amber-700 mt-2 border-t border-slate-200 pt-2"><span>Discount ({doc.discount_pct}%)</span><span>−{fmtInr(discount)}</span></div>}
              <div className="flex justify-between py-1 text-sm text-slate-700"><span>GST ({doc.gst_rate}%)</span><span className="font-semibold">{fmtInr(tax)}</span></div>

              <div className="flex justify-between py-3 mt-2 border-t-2 border-slate-900">
                <span className="font-bold text-base" style={{fontFamily: 'Outfit, sans-serif'}}>Total</span>
                <span className="text-2xl font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}}>{fmtInr(total)}</span>
              </div>
            </div>
          </section>
        </div>

        {/* Sticky action bar */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-8 py-4 z-20">
          {/* Phase Hotfix-1 — error banner replaces the silent crash. Renders the parsed string only — never a raw 422 object. */}
          {saveError && (
            <div data-testid="billing-save-error" className="mb-3 p-3 bg-red-50 border border-red-300 text-red-800 text-sm rounded">
              <div className="font-semibold mb-0.5">Save failed</div>
              <div className="break-words">{String(saveError)}</div>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={() => setEditor(null)} disabled={saving} className="rounded border border-slate-300 px-5 py-2.5 hover:bg-slate-50 text-sm disabled:opacity-50">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="billing-save" className="rounded bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-2.5 font-bold flex items-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed">
              <Check size={16} weight="bold" /> {saving ? "Saving…" : `Save ${docLabel}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------- What's Next banner (shown after successful create) -------- */
const WhatsNextBanner = ({ info, onClose, onView, onConvert, onRecordPayment, onShareEmail, onShareWhatsApp, onCopyLink, user }) => {
  const canConvert = ["admin", "manager", "bdm", "bde", "cfo"].includes(user?.role);
  const canPay = ["admin", "cfo"].includes(user?.role);
  return (
    <div data-testid="whats-next-banner" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="border-l-4 border-green-500 px-6 py-5 bg-green-50/40">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-green-500 text-white flex items-center justify-center"><Check size={18} weight="bold" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-green-700 font-bold">Created successfully</div>
              <div className="text-lg font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{info.number}</div>
            </div>
          </div>
          <p className="text-sm text-slate-700 mt-2">What's Next?</p>
        </div>
        <div className="p-5 grid grid-cols-1 gap-2.5">
          <button data-testid="wn-view" onClick={onView} className="text-left border border-slate-200 hover:border-[#002FA7] hover:bg-slate-50 rounded px-4 py-3 flex items-center gap-3 transition-all">
            <Printer size={18} className="text-[#002FA7]" />
            <div><div className="text-sm font-bold">View / Print / PDF</div><div className="text-xs text-slate-500">Open the document for review or print</div></div>
          </button>

          {/* Share row */}
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mt-1 mb-0.5">Share with customer</div>
          <div className="grid grid-cols-3 gap-2">
            <button data-testid="wn-share-email" onClick={onShareEmail} className="border border-slate-200 hover:border-[#002FA7] hover:bg-slate-50 rounded px-3 py-3 flex flex-col items-center gap-1 transition-all">
              <Envelope size={20} className="text-[#002FA7]" />
              <span className="text-[11px] font-bold">Email</span>
            </button>
            <button data-testid="wn-share-whatsapp" onClick={onShareWhatsApp} className="border border-slate-200 hover:border-green-600 hover:bg-green-50 rounded px-3 py-3 flex flex-col items-center gap-1 transition-all">
              <WhatsappLogo size={20} className="text-green-600" weight="fill" />
              <span className="text-[11px] font-bold">WhatsApp</span>
            </button>
            <button data-testid="wn-copy-link" onClick={onCopyLink} className="border border-slate-200 hover:border-[#002FA7] hover:bg-slate-50 rounded px-3 py-3 flex flex-col items-center gap-1 transition-all">
              <LinkIcon size={20} className="text-[#002FA7]" />
              <span className="text-[11px] font-bold">Copy Link</span>
            </button>
          </div>

          {canConvert && info.doc_type === "estimation" && (
            <button data-testid="wn-convert-proforma" onClick={() => onConvert("proforma")} className="text-left border border-slate-200 hover:border-[#002FA7] hover:bg-slate-50 rounded px-4 py-3 flex items-center gap-3 transition-all mt-1">
              <ArrowRight size={18} className="text-[#002FA7]" />
              <div><div className="text-sm font-bold">Convert to Proforma</div><div className="text-xs text-slate-500">Proceeds only after approval</div></div>
            </button>
          )}
          {canConvert && ["estimation", "proforma"].includes(info.doc_type) && (
            <button data-testid="wn-convert-invoice" onClick={() => onConvert("invoice")} className="text-left border border-slate-200 hover:border-[#002FA7] hover:bg-slate-50 rounded px-4 py-3 flex items-center gap-3 transition-all">
              <Receipt size={18} className="text-[#002FA7]" />
              <div><div className="text-sm font-bold">Convert to Invoice</div><div className="text-xs text-slate-500">Generate tax invoice for the customer</div></div>
            </button>
          )}
          {canPay && info.doc_type === "invoice" && (
            <button data-testid="wn-record-payment" onClick={onRecordPayment} className="text-left border border-slate-200 hover:border-[#002FA7] hover:bg-slate-50 rounded px-4 py-3 flex items-center gap-3 transition-all">
              <Money size={18} className="text-[#002FA7]" />
              <div><div className="text-sm font-bold">Record Payment</div><div className="text-xs text-slate-500">Mark payment as received</div></div>
            </button>
          )}
          <button onClick={onClose} className="text-center text-xs text-slate-500 hover:text-slate-800 mt-1 py-2">Dismiss</button>
        </div>
      </div>
    </div>
  );
};

/* -------- Share Doc Modal (Email / WhatsApp) -------- */
const ShareDocModal = ({ doc, mode, onClose, user, onSent }) => {
  const isEmail = mode === "email";
  const [form, setForm] = useState(
    isEmail
      ? { to: doc.customer_email || "", subject: "", message: "" }
      : { phone: doc.customer_phone || "", message: "" }
  );
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      if (isEmail) {
        if (!form.to) { toast.error("Email is required"); setSending(false); return; }
        const r = await api.post(`/billing/${doc.doc_id}/send-email`, {
          to: form.to, subject: form.subject, message: form.message,
        });
        toast.success(r.data.status === "sent" ? "Email sent!" : "Email queued (mock mode — add Zoho creds to send)");
      } else {
        if (!form.phone) { toast.error("Phone number required"); setSending(false); return; }
        const r = await api.post(`/billing/${doc.doc_id}/share-whatsapp`, {
          phone: form.phone, message: form.message,
        });
        if (r.data.wa_url) {
          window.open(r.data.wa_url, "_blank", "noopener,noreferrer");
          toast.success("Opening WhatsApp…");
        }
      }
      onSent?.();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid={`share-modal-${mode}`}>
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {isEmail ? <Envelope size={18} className="text-[#002FA7]" /> : <WhatsappLogo size={18} className="text-green-600" weight="fill" />}
            <h2 className="text-lg font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Share {doc.number} via {isEmail ? "Email" : "WhatsApp"}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {isEmail ? (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Customer Email <span className="text-red-500">*</span></label>
                <input data-testid="share-email-to" type="email" value={form.to} onChange={e => setForm(f => ({ ...f, to: e.target.value }))}
                  className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2.5 outline-none text-sm" placeholder="customer@example.com" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Subject (optional)</label>
                <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                  className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2.5 outline-none text-sm"
                  placeholder={`Quotation ${doc.number} from SwifTech India`} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Message (optional)</label>
                <textarea rows={4} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2.5 outline-none text-sm"
                  placeholder="Add a personal note for your customer…" />
              </div>
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                Email includes a secure link to view, print or accept the quotation.
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Customer Phone <span className="text-red-500">*</span></label>
                <input data-testid="share-whatsapp-phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2.5 outline-none text-sm" placeholder="+91 98765 43210" />
                <div className="text-[11px] text-slate-500 mt-1">Include country code. Numbers without + will default to India (+91).</div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Custom Message (optional)</label>
                <textarea rows={4} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2.5 outline-none text-sm"
                  placeholder="Leave blank for default message with share link" />
              </div>
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
                Opens WhatsApp with pre-filled message + secure share link.
              </div>
            </>
          )}
        </div>
        <div className="border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="rounded border border-slate-300 px-4 py-2 hover:bg-slate-50 text-sm">Cancel</button>
          <button onClick={send} disabled={sending} data-testid={`share-${mode}-send`}
            className={`rounded text-white px-5 py-2 font-bold text-sm disabled:opacity-60 flex items-center gap-2 ${isEmail ? "bg-[#002FA7] hover:bg-[#00227A]" : "bg-green-600 hover:bg-green-700"}`}>
            {isEmail ? <Envelope size={14} /> : <WhatsappLogo size={14} weight="fill" />}
            {sending ? "Sending…" : (isEmail ? "Send Email" : "Open WhatsApp")}
          </button>
        </div>
      </div>
    </div>
  );
};

/* -------- Doc Viewer (legacy — superseded by Zoho-style viewer above) -------- */
const LegacyDocViewer = () => null;
// eslint-disable-next-line no-lone-blocks
{ /* legacy viewer JSX removed */ }

/* -------- Number → words (Indian number system) -------- */
const numToIndianWords = (num) => {
  if (num === 0) return "Zero";
  num = Math.round(Number(num) * 100) / 100;
  const [intPart, decPart] = num.toFixed(2).split(".");
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const twoDigit = (n) => n < 20 ? a[n] : `${b[Math.floor(n/10)]}${n%10 ? " " + a[n%10] : ""}`;
  const threeDigit = (n) => n >= 100 ? `${a[Math.floor(n/100)]} Hundred${n%100 ? " " + twoDigit(n%100) : ""}` : twoDigit(n);
  const n = parseInt(intPart, 10);
  const crore = Math.floor(n / 10000000), rest1 = n % 10000000;
  const lakh = Math.floor(rest1 / 100000), rest2 = rest1 % 100000;
  const thousand = Math.floor(rest2 / 1000), rest3 = rest2 % 1000;
  let out = "";
  if (crore) out += threeDigit(crore) + " Crore ";
  if (lakh) out += threeDigit(lakh) + " Lakh ";
  if (thousand) out += threeDigit(thousand) + " Thousand ";
  if (rest3) out += threeDigit(rest3);
  out = out.trim() || "Zero";
  const paise = parseInt(decPart, 10);
  return `${out} Rupees${paise ? ` and ${twoDigit(paise)} Paise` : ""} only`;
};

/* -------- Doc Viewer (Zoho-style, print-ready) -------- */
const DocViewer = ({ doc, settings, onClose, fmtInr, onShareEmail, onShareWhatsApp, onCopyLink, userRole }) => {
  const typeLabel = { estimation: "ESTIMATE", proforma: "PROFORMA INVOICE", invoice: "TAX INVOICE" }[doc.doc_type] || "DOCUMENT";
  const s = settings || {};
  const accent = s.template_accent_color || "#002FA7";
  const style = s.template_style || "standard";
  const showHsn = s.template_show_hsn !== false;
  const showDiscount = s.template_show_discount !== false;
  const showBank = s.template_show_bank !== false;
  const showSignature = s.template_show_signature !== false;
  const showWords = s.template_show_amount_words !== false;
  const showQR = !!s.template_show_qr;
  const compact = style === "compact";
  const classic = style === "classic";
  // Same-state GST split: CGST + SGST; otherwise IGST.
  // Prefer place_of_supply state code; fallback to customer GSTIN first 2 chars.
  const sellerStateCode = (s.state_code || s.gstin?.slice(0, 2) || "").toString();
  const posStateCode = (doc.place_of_supply || "").split("-")[0] || "";
  const buyerStateCode = posStateCode || (doc.customer_gstin || "").slice(0, 2);
  const isInterState = !!(buyerStateCode && sellerStateCode && buyerStateCode !== sellerStateCode);
  const gstRate = Number(doc.gst_rate || s.default_gst_rate || 0);
  const cgst = isInterState ? 0 : (doc.tax_amount || 0) / 2;
  const sgst = isInterState ? 0 : (doc.tax_amount || 0) / 2;
  const igst = isInterState ? (doc.tax_amount || 0) : 0;
  const totalDue = (doc.total || 0) - (doc.amount_paid || 0);
  const upiQrUrl = showQR && s.upi_id ? `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`upi://pay?pa=${s.upi_id}&pn=${s.company_name || "SwifTech"}&am=${totalDue}&cu=INR&tn=${doc.number}`)}` : "";

  return createPortal(
    <div className="invoice-print-root fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white shadow-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} id="zoho-invoice-doc">
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center no-print sticky top-0 bg-white z-10">
          <h2 className="font-bold">{typeLabel} · {doc.number}</h2>
          <div className="flex gap-2 items-center flex-wrap">
            {onShareEmail && <button onClick={onShareEmail} data-testid="docviewer-share-email" title="Send via Email" className="border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-1.5"><Envelope size={14} /> Email</button>}
            {onShareWhatsApp && <button onClick={onShareWhatsApp} data-testid="docviewer-share-whatsapp" title="Share via WhatsApp" className="border border-slate-300 px-3 py-2 text-sm hover:bg-green-50 hover:border-green-500 flex items-center gap-1.5 text-green-700"><WhatsappLogo size={14} weight="fill" /> WhatsApp</button>}
            {onCopyLink && <button onClick={onCopyLink} data-testid="docviewer-copy-link" title="Copy secure share link" className="border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-1.5"><Copy size={14} /> Copy Link</button>}
            <button onClick={() => {
              // Scope the print-only CSS via a body class so only the invoice modal prints.
              // Other window.print() callers in the app are unaffected.
              const root = document.documentElement;
              root.classList.add("billing-printing");
              const onAfter = () => { root.classList.remove("billing-printing"); window.removeEventListener("afterprint", onAfter); };
              window.addEventListener("afterprint", onAfter);
              setTimeout(() => window.print(), 0);
            }} data-testid="invoice-print-btn" className="rounded-none border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"><Printer size={14} /> Print / PDF</button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
          </div>
        </div>

        {/* PHASE 7 START — additive lifecycle progress + next-step hint + audit timeline */}
        <div className="px-6 pt-4 space-y-3 print:hidden" data-testid="phase7-billing-extras">
          <BillingFlowProgress doc={doc} />
          <BillingNextStepHint doc={doc} role={userRole} />
          <BillingAuditTimeline entityType="billing_doc" entityId={doc.doc_id} role={userRole} />
        </div>
        {/* PHASE 7 END */}

        {/* ZOHO-STYLE INVOICE BODY */}
        <div className={`p-8 print:p-10 zoho-invoice relative ${compact ? "text-[13px]" : ""} ${classic ? "border border-slate-900 m-4" : ""}`} style={{ fontFamily: "Inter, Outfit, sans-serif" }}>
          {doc.letterhead_data_url && (
            <img src={doc.letterhead_data_url} alt="" aria-hidden="true"
              className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
              style={{ opacity: doc.letterhead_opacity || 0.08, zIndex: 0 }} />
          )}
          <div className="relative" style={{ zIndex: 1 }}>
          {/* Header */}
          <div className="flex justify-between items-start mb-6 pb-6" style={{ borderBottom: `2px solid ${accent}` }}>
            <div className="flex gap-4 items-start">
              {s.logo_url && <img src={s.logo_url} alt="Logo" className="w-20 h-20 object-contain" />}
              <div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.company_name || "SwifTech India"}</h1>
                {s.tagline && <p className="text-xs text-slate-600 mt-0.5">{s.tagline}</p>}
                <div className="text-xs text-slate-600 mt-2 whitespace-pre-line">
                  {[s.address_line1, s.address_line2].filter(Boolean).join(", ")}
                  {(s.city || s.state) && <div>{[s.city, s.state, s.pincode].filter(Boolean).join(", ")}</div>}
                  {s.country && <div>{s.country}</div>}
                  {s.phone && <div>📞 {s.phone}</div>}
                  {s.email && <div>✉ {s.email}</div>}
                  {s.gstin && <div className="font-mono mt-1"><b>GSTIN:</b> {s.gstin}</div>}
                  {s.pan && <div className="font-mono"><b>PAN:</b> {s.pan}</div>}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-block bg-slate-100 px-4 py-2 border border-slate-300">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">{typeLabel}</div>
                <div className="text-2xl font-bold mt-0.5" style={{ fontFamily: 'Outfit, sans-serif', color: accent }}>#{doc.number}</div>
              </div>
              <table className="text-xs mt-3 ml-auto">
                <tbody>
                  <tr><td className="pr-3 text-slate-500">Date</td><td className="font-semibold">{(doc.created_at || "").slice(0, 10)}</td></tr>
                  {doc.reference_no && <tr><td className="pr-3 text-slate-500">Reference#</td><td className="font-semibold font-mono">{doc.reference_no}</td></tr>}
                  {doc.valid_until && <tr><td className="pr-3 text-slate-500">{doc.doc_type === "invoice" ? "Due Date" : "Valid Till"}</td><td className="font-semibold">{doc.valid_until}</td></tr>}
                  {doc.expiry_date && doc.doc_type !== "invoice" && <tr><td className="pr-3 text-slate-500">Expiry Date</td><td className="font-semibold">{doc.expiry_date}</td></tr>}
                  {doc.salesperson && <tr><td className="pr-3 text-slate-500">Salesperson</td><td className="font-semibold">{doc.salesperson}</td></tr>}
                  {doc.place_of_supply && <tr><td className="pr-3 text-slate-500">Place of Supply</td><td className="font-semibold">{doc.place_of_supply}</td></tr>}
                  <tr><td className="pr-3 text-slate-500">Status</td><td className="font-semibold uppercase">{doc.status}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Bill To */}
          <div className="grid grid-cols-2 gap-8 mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 font-bold">Bill To</div>
              <div className="font-bold text-base">{doc.customer_name}</div>
              {doc.customer_address && <div className="text-xs text-slate-700 mt-1 whitespace-pre-line">{doc.customer_address}</div>}
              {doc.customer_email && <div className="text-xs mt-1">{doc.customer_email}</div>}
              {doc.customer_phone && <div className="text-xs">{doc.customer_phone}</div>}
              {doc.customer_gstin && <div className="text-xs font-mono mt-1"><b>GSTIN:</b> {doc.customer_gstin}</div>}
            </div>
            <div className="text-right">
              {doc.doc_type === "invoice" && (
                <div className="bg-amber-50 border border-amber-200 px-4 py-3 inline-block text-left">
                  <div className="text-[11px] uppercase tracking-wider text-amber-700 font-bold">Balance Due</div>
                  <div className="text-2xl font-bold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{fmtInr(totalDue)}</div>
                </div>
              )}
            </div>
          </div>

          {/* Subject */}
          {doc.subject && (
            <div className="mb-5 border-l-4 pl-3 text-sm" style={{ borderColor: accent }}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Subject</div>
              <div className="text-slate-800">{doc.subject}</div>
            </div>
          )}

          {/* Line items */}
          <table className="w-full mb-6 text-sm">
            <thead>
              <tr className="text-white text-xs uppercase tracking-wider" style={{ backgroundColor: accent }}>
                <th className="text-left p-2">#</th>
                <th className="text-left p-2">Item / Description</th>
                {showHsn && <th className="text-center p-2">HSN</th>}
                <th className="text-right p-2">Qty</th>
                <th className="text-right p-2">Rate</th>
                {showDiscount && <th className="text-right p-2">Disc %</th>}
                <th className="text-right p-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, i) => {
                const rowKey = li.row_id || li.product_id || `${doc.doc_id}-row-${i}`;
                return (
                  <tr key={rowKey} className="border-b border-slate-200">
                    <td className="p-2">{i + 1}</td>
                    <td className="p-2 whitespace-pre-wrap break-words align-top">{li.description}</td>
                    {showHsn && <td className="p-2 text-center text-xs">{li.hsn_code || "-"}</td>}
                    <td className="p-2 text-right">{li.quantity}</td>
                    <td className="p-2 text-right">{fmtInr(li.unit_price)}</td>
                    {showDiscount && <td className="p-2 text-right">{li.discount || 0}</td>}
                    <td className="p-2 text-right font-semibold">{fmtInr(li.line_total || (li.quantity * li.unit_price) * (1 - (Number(li.discount) || 0) / 100))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-6">
            <div className="w-80 text-sm">
              <div className="flex justify-between py-1"><span className="text-slate-500">Sub Total</span><span>{fmtInr(doc.subtotal)}</span></div>
              {isInterState ? (
                <div className="flex justify-between py-1"><span className="text-slate-500">IGST ({gstRate}%)</span><span>{fmtInr(igst)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between py-1"><span className="text-slate-500">CGST ({gstRate / 2}%)</span><span>{fmtInr(cgst)}</span></div>
                  <div className="flex justify-between py-1"><span className="text-slate-500">SGST ({gstRate / 2}%)</span><span>{fmtInr(sgst)}</span></div>
                </>
              )}
              <div className="flex justify-between py-2 mt-1 bg-slate-100 px-3 border-y-2 border-slate-800 font-bold"><span>Total</span><span>{fmtInr(doc.total)}</span></div>
              {(doc.amount_paid || 0) > 0 && (
                <>
                  <div className="flex justify-between py-1 text-green-700"><span>Paid</span><span>{fmtInr(doc.amount_paid)}</span></div>
                  <div className="flex justify-between py-1 font-bold border-t border-slate-300"><span>Balance</span><span>{fmtInr(totalDue)}</span></div>
                </>
              )}
            </div>
          </div>

          {/* Amount in words */}
          {showWords && (
            <div className="text-xs border-t border-b border-slate-200 py-3 mb-5">
              <span className="text-slate-500 uppercase tracking-wider font-bold mr-2">Total in words:</span>
              <span className="italic">{numToIndianWords(doc.total)}</span>
            </div>
          )}

          {/* Notes & Terms — render HTML from template/doc, fallback to plain text */}
          {(doc.notes_html || doc.notes || s.default_notes || doc.terms_html || doc.terms || s.terms) && (
            <div className="grid grid-cols-2 gap-6 text-xs mb-6">
              {(doc.notes_html || doc.notes || s.default_notes) && (
                <div>
                  <div className="uppercase tracking-wider text-slate-500 font-bold mb-1">Notes</div>
                  {doc.notes_html ? (
                    <div className="doc-rich" {...safeHtmlProps(doc.notes_html)} />
                  ) : (
                    <p className="whitespace-pre-line">{doc.notes || s.default_notes}</p>
                  )}
                </div>
              )}
              {(doc.terms_html || doc.terms || s.terms) && (
                <div>
                  <div className="uppercase tracking-wider text-slate-500 font-bold mb-1">Terms &amp; Conditions</div>
                  {doc.terms_html ? (
                    <div className="doc-rich" {...safeHtmlProps(doc.terms_html)} />
                  ) : (
                    <p className="whitespace-pre-line">{doc.terms || s.terms}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Bank & Payment */}
          {showBank && (s.bank_name || s.upi_id) && (
            <div className="border border-slate-200 p-3 text-xs bg-slate-50 mb-6 flex items-start gap-4">
              <div className="flex-1">
                <div className="uppercase tracking-wider text-slate-500 font-bold mb-1">Payment Details</div>
                <div className="grid grid-cols-2 gap-2">
                  {s.bank_name && <div><b>Bank:</b> {s.bank_name}</div>}
                  {s.account_number && <div><b>A/C:</b> {s.account_number}</div>}
                  {s.ifsc && <div><b>IFSC:</b> {s.ifsc}</div>}
                  {s.upi_id && <div><b>UPI:</b> {s.upi_id}</div>}
                </div>
              </div>
              {upiQrUrl && <img src={upiQrUrl} alt="UPI QR" className="w-24 h-24 border border-slate-200 bg-white" />}
            </div>
          )}

          {/* Signature */}
          {showSignature && (s.signature_name || s.signature_title) && (
            <div className="flex justify-end mt-10">
              <div className="text-center">
                <div className="border-t-2 border-slate-400 pt-1 px-10 text-xs">
                  <div className="font-bold">{s.signature_name || "Authorised Signatory"}</div>
                  {s.signature_title && <div className="text-slate-500">{s.signature_title}</div>}
                  <div className="text-slate-500 uppercase tracking-wider mt-0.5 text-[10px]">For {s.company_name || "SwifTech India"}</div>
                </div>
              </div>
            </div>
          )}

          {(doc.footer_html || s.template_footer) && (
            <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-600 doc-rich">
              {doc.footer_html
                ? <div {...safeHtmlProps(doc.footer_html)} />
                : <div className="whitespace-pre-line text-center">{s.template_footer}</div>}
            </div>
          )}

          <div className="mt-6 text-center text-[11px] text-slate-400">This is a computer-generated document and does not require a physical signature.</div>
          </div>
        </div>
      </div>
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          /* Default print rules retained for any non-billing print use */
          .no-print { display: none !important; }
          body { background: white !important; }
          #zoho-invoice-doc { box-shadow: none !important; max-width: none !important; max-height: none !important; }
        }
        /* Scoped, strong print rules — active only while the user clicks Print/PDF
           on the invoice viewer. The .invoice-print-root is portaled to document.body
           so it is a direct body child; we can therefore use display:none on every
           other body child to physically collapse the page height to just the invoice. */
        @media print {
          html.billing-printing body > *:not(.invoice-print-root) { display: none !important; }
          html.billing-printing .invoice-print-root {
            position: static !important;
            inset: auto !important;
            background: white !important;
            padding: 0 !important;
            display: block !important;
            z-index: auto !important;
          }
          html.billing-printing .invoice-print-root > div {
            box-shadow: none !important;
            max-width: none !important;
            max-height: none !important;
            overflow: visible !important;
            width: 100% !important;
            border: 0 !important;
          }
          html.billing-printing .no-print,
          html.billing-printing [data-no-print],
          html.billing-printing [data-testid="phase7-billing-extras"] {
            display: none !important;
          }
          /* Page-break hygiene for long line-item tables */
          html.billing-printing table { page-break-inside: auto; }
          html.billing-printing tr { page-break-inside: avoid; page-break-after: auto; }
          html.billing-printing thead { display: table-header-group; }
          html.billing-printing tfoot { display: table-footer-group; }
        }
        .doc-rich ul { list-style: disc; padding-left: 1.25rem; margin: 0.25rem 0; }
        .doc-rich ol { list-style: decimal; padding-left: 1.5rem; margin: 0.25rem 0; }
        .doc-rich a { color: #002FA7; text-decoration: underline; }
        .doc-rich p { margin: 0.25rem 0; }
        .doc-rich b, .doc-rich strong { font-weight: 700; }
      `}</style>
    </div>,
    document.body
  );
};

/* -------- Company Settings Tab -------- */
const numberingExample = (f) => {
  const prefix = f.estimation_prefix || "EST";
  const len = Math.max(1, Math.min(Number(f.numbering_seq_length) || 6, 12));
  const seq = "1".padStart(len, "0");
  const now = new Date();
  const y = now.getFullYear();
  const fyStart = now.getMonth() >= 3 ? y : y - 1;   // Indian FY starts April
  const fyEnd = fyStart + 1;
  let token = "";
  switch (f.numbering_year_format || "calendar") {
    case "none": token = ""; break;
    case "fy": token = `-${fyStart}-${String(fyEnd).slice(-2)}`; break;
    case "fy_short": token = `-${String(fyStart).slice(-2)}-${String(fyEnd).slice(-2)}`; break;
    default: token = `-${y}`;                           // calendar
  }
  return `${prefix}${token}-${seq}`;
};

const CompanySettingsTab = ({ user, settings, onSaved }) => {
  const [form, setForm] = useState(settings || {});
  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  const canEdit = user.role === "admin";
  const update = (patch) => setForm({ ...form, ...patch });

  const save = async () => {
    try {
      await axios.put(`${API}/company-settings?requester_role=${user.role}`, form);
      toast.success("Company settings saved");
      // Invalidate client-side WhatsApp cache so catalogue picks up new number/templates immediately
      try {
        const { invalidateCompanySettings } = await import("../utils/whatsappHelper");
        invalidateCompanySettings();
      } catch { /* helper optional */ }
      onSaved(form);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (!form) return <div className="py-12 text-center"><div className="loading-spinner" /></div>;

  const field = (key, label, type = "text", full = false) => (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">{label}</label>
      {type === "textarea" ? (
        <textarea value={form[key] || ""} disabled={!canEdit}
          onChange={e => update({ [key]: e.target.value })}
          data-testid={`cs-${key}`} rows={3}
          className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 text-sm outline-none disabled:bg-slate-50" />
      ) : (
        <input type={type} value={form[key] || ""} disabled={!canEdit}
          onChange={e => update({ [key]: type === "number" ? Number(e.target.value) : e.target.value })}
          data-testid={`cs-${key}`}
          className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 text-sm outline-none disabled:bg-slate-50" />
      )}
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 shadow-sm p-6" data-testid="company-settings-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold">Company Settings</h2>
          <p className="text-xs text-slate-500">These defaults are used on every estimate, proforma, and invoice. {!canEdit && "(Admin only)"}</p>
        </div>
        {form.logo_url && <img src={form.logo_url} alt="Logo" className="w-16 h-16 object-contain border border-slate-200" />}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {field("company_name", "Company Name")}
        {field("tagline", "Tagline")}
        {field("address_line1", "Address line 1", "text", true)}
        {field("address_line2", "Address line 2", "text", true)}
        {field("city", "City")}
        {field("state", "State")}
        {field("pincode", "Pincode")}
        {field("country", "Country")}
        {field("phone", "Phone")}
        {field("email", "Email")}
        {field("website", "Website")}
        {field("gstin", "GSTIN")}
        {field("pan", "PAN")}
        {field("state_code", "GST State Code (2-digit)")}
        {field("default_gst_rate", "Default GST Rate %", "number")}
        {field("bank_name", "Bank Name")}
        {field("account_number", "Account Number")}
        {field("ifsc", "IFSC")}
        {field("upi_id", "UPI ID")}
        {field("signature_name", "Authorised Signatory Name")}
        {field("signature_title", "Signatory Title")}
        {field("logo_url", "Logo URL (default /assets/swiftech-logo.jpg)", "text", true)}
        {field("default_notes", "Default Notes on Invoices", "textarea", true)}
        {field("terms", "Terms & Conditions", "textarea", true)}
      </div>

      {/* Quotation / Document Numbering */}
      <div className="mt-6 pt-6 border-t border-slate-200" data-testid="cs-numbering-section">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-1">Quotation Numbering</h3>
        <p className="text-xs text-slate-500 mb-4">Applies to NEW documents only — existing numbers never change. Numbers are generated atomically (concurrency-safe) and are never recycled.</p>
        <div className="grid grid-cols-2 gap-4">
          {field("estimation_prefix", "Document Prefix (e.g. EST / QT / QUO)")}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Year Format</label>
            <select value={form.numbering_year_format || "calendar"} disabled={!canEdit} data-testid="cs-numbering_year_format"
              onChange={e => update({ numbering_year_format: e.target.value })}
              className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 text-sm outline-none disabled:bg-slate-50">
              <option value="none">None — EST-000001</option>
              <option value="calendar">Calendar Year (YYYY) — EST-2026-000001</option>
              <option value="fy">Financial Year (YYYY-YY) — EST-2026-27-000001</option>
              <option value="fy_short">Short FY (YY-YY) — EST-26-27-000001</option>
            </select>
          </div>
          {field("numbering_seq_length", "Running Number Digits (e.g. 6 → 000001)", "number")}
          <div className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={!!form.numbering_reset_yearly} disabled={!canEdit} id="cs-reset-yearly" data-testid="cs-numbering_reset_yearly"
              onChange={e => update({ numbering_reset_yearly: e.target.checked })} className="w-4 h-4" />
            <label htmlFor="cs-reset-yearly" className="text-sm text-slate-700">Reset running number every year / FY</label>
          </div>
        </div>
        <div className="mt-3 text-sm bg-slate-50 border border-slate-200 px-3 py-2 inline-block" data-testid="cs-numbering-preview">
          Example next number: <span className="font-mono font-bold text-[#002FA7]">{numberingExample(form)}</span>
        </div>
      </div>

      {/* Template customization */}
      <div className="mt-6 pt-6 border-t border-slate-200">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-600 mb-1">Invoice / Quote Template</h3>
        <p className="text-xs text-slate-500 mb-4">Customize how the printable PDF looks.</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Accent Color</label>
            <div className="flex gap-2 items-center">
              <input type="color" value={form.template_accent_color || "#002FA7"} disabled={!canEdit}
                onChange={e => update({ template_accent_color: e.target.value })}
                data-testid="cs-template_accent_color" className="h-11 w-16 rounded-none border border-slate-300 cursor-pointer" />
              <input type="text" value={form.template_accent_color || "#002FA7"} disabled={!canEdit}
                onChange={e => update({ template_accent_color: e.target.value })}
                className="flex-1 rounded-none border border-slate-300 p-2 text-sm outline-none font-mono" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Template Style</label>
            <select value={form.template_style || "standard"} disabled={!canEdit}
              onChange={e => update({ template_style: e.target.value })}
              data-testid="cs-template_style" className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
              <option value="standard">Standard (Zoho-style)</option>
              <option value="compact">Compact (less whitespace)</option>
              <option value="classic">Classic (traditional Indian GST)</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="cs-template_show_hsn" checked={form.template_show_hsn !== false} disabled={!canEdit}
              onChange={e => update({ template_show_hsn: e.target.checked })} />
            Show HSN / SAC column
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="cs-template_show_discount" checked={form.template_show_discount !== false} disabled={!canEdit}
              onChange={e => update({ template_show_discount: e.target.checked })} />
            Show Discount column
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.template_show_bank !== false} disabled={!canEdit}
              onChange={e => update({ template_show_bank: e.target.checked })} />
            Show Bank / UPI details
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.template_show_signature !== false} disabled={!canEdit}
              onChange={e => update({ template_show_signature: e.target.checked })} />
            Show Signature block
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.template_show_amount_words !== false} disabled={!canEdit}
              onChange={e => update({ template_show_amount_words: e.target.checked })} />
            Show Amount in Words
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.template_show_qr} disabled={!canEdit}
              onChange={e => update({ template_show_qr: e.target.checked })} />
            Show UPI QR Code
          </label>
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Footer Text (shown below signature)</label>
            <textarea value={form.template_footer || ""} disabled={!canEdit}
              onChange={e => update({ template_footer: e.target.value })}
              data-testid="cs-template_footer" rows={2}
              placeholder="E.g. Thank you for your business. Subject to Mumbai jurisdiction."
              className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
          </div>
        </div>
      </div>

      {/* WhatsApp Quick-Enquiry (Phase 6) */}
      <div className="mt-6 pt-5 border-t border-slate-200">
        <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">WhatsApp Quick-Enquiry (Catalogue)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Company WhatsApp Number</label>
            <input value={form.whatsapp_number || ""} disabled={!canEdit}
              onChange={e => update({ whatsapp_number: e.target.value })}
              data-testid="cs-whatsapp_number" placeholder="e.g. 919999999999 (country code + number, digits only)"
              className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
            <p className="text-[11px] text-slate-500 mt-1">Used by catalogue card + Product Detail "WhatsApp" buttons.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Enquiry Message Template</label>
            <textarea value={form.whatsapp_enquiry_template || ""} disabled={!canEdit}
              onChange={e => update({ whatsapp_enquiry_template: e.target.value })}
              data-testid="cs-whatsapp_enquiry_template" rows={2}
              placeholder="Tokens: {product} {model} {quantity}"
              className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Lead Follow-up Template (from Leads drawer)</label>
            <textarea value={form.whatsapp_followup_template || ""} disabled={!canEdit}
              onChange={e => update({ whatsapp_followup_template: e.target.value })}
              data-testid="cs-whatsapp_followup_template" rows={2}
              placeholder="Tokens: {name} {product} {quantity}"
              className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
          </div>
        </div>
      </div>
      {canEdit && (
        <div className="flex justify-end mt-5 pt-5 border-t border-slate-200">
          <button onClick={save} data-testid="cs-save" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-2.5 font-bold">Save Company Settings</button>
        </div>
      )}
    </div>
  );
};

/* -------- Payment Modal -------- */
const PaymentModal = ({ invoice, user, onClose, onSaved, fmtInr }) => {
  const balance = (Number(invoice.total) || 0) - (Number(invoice.amount_paid) || 0);
  const [form, setForm] = useState({ amount: balance, method: "bank_transfer", reference: "", paid_on: new Date().toISOString().slice(0, 10), notes: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      // recorded_by / recorded_by_name / recorder_role are stamped server-side from JWT
      await api.post(`/billing/${invoice.doc_id}/payments`, {
        ...form, amount: Number(form.amount)
      });
      toast.success("Payment recorded");
      onClose();
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Record Payment</div>
            <div className="font-bold">{invoice.number} · {fmtInr(invoice.total)}</div>
            <div className="text-xs text-slate-500">Balance: {fmtInr(balance)}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Amount *</label>
            <input data-testid="payment-amount" type="number" step="0.01" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-3 outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-1">Method</label>
            <select data-testid="payment-method" value={form.method} onChange={e => setForm({ ...form, method: e.target.value })} className="w-full rounded-none border border-slate-300 p-3 outline-none">
              <option value="bank_transfer">Bank Transfer (NEFT/RTGS/IMPS)</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="cash">Cash</option>
              <option value="card">Card</option>
            </select>
          </div>
          <input placeholder="Reference / UTR / Cheque No." value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} className="w-full rounded-none border border-slate-300 p-3 outline-none" />
          <input type="date" value={form.paid_on} onChange={e => setForm({ ...form, paid_on: e.target.value })} className="w-full rounded-none border border-slate-300 p-3 outline-none" />
          <textarea placeholder="Notes" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full rounded-none border border-slate-300 p-3 outline-none" />
          <button type="submit" data-testid="payment-submit" className="w-full rounded-none bg-green-600 text-white hover:bg-green-700 px-6 py-3 font-bold flex items-center justify-center gap-2"><Money size={16} weight="fill" /> Record Payment</button>
        </form>
      </div>
    </div>
  );
};

/* -------- Collection Report -------- */
const CollectionReport = ({ data, fmtInr }) => {
  const [month, setMonth] = useState(data?.month || new Date().toISOString().slice(0, 7));
  const [r, setR] = useState(data);

  const load = useCallback(async (m) => {
    try {
      const res = await axios.get(`${API}/billing-reports/collection?month=${m}`);
      setR(res.data);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { if (month !== r?.month) load(month); }, [month, r?.month, load]);

  if (!r) return <div className="flex justify-center py-12"><div className="loading-spinner"></div></div>;
  return (
    <div className="space-y-6" data-testid="collection-report">
      <div className="flex items-center gap-4 flex-wrap">
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Month</label>
        <input data-testid="collection-month" type="month" value={month} onChange={e => setMonth(e.target.value)} className="rounded-none border border-slate-300 p-2 outline-none" />
        <span className="text-xs text-slate-500 ml-auto">{r.month}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatBox label="Collected" value={fmtInr(r.total_collected)} color="#10B981" />
        <StatBox label="Outstanding" value={fmtInr(r.outstanding)} subtitle={`${r.outstanding_count} invoices`} color="#DC2626" />
        <StatBox label="Recorded Payments" value={r.recent_payments.length} color="#002FA7" />
      </div>
      <div className="bg-white border border-slate-200 p-6">
        <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>6-Month Collection Trend</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={r.trend_6m}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="month" tick={CHART_TICK_XS} />
            <YAxis tick={CHART_TICK_XS} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [fmtInr(v), "Collected"]} />
            <Line type="monotone" dataKey="amount" stroke="#002FA7" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>By Day — {r.month}</h3>
          {r.per_day.length === 0 ? <p className="text-sm text-slate-500">No payments this month</p> :
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={r.per_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={CHART_TICK_XS} />
                <YAxis tick={CHART_TICK_XS} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [fmtInr(v), "Collected"]} />
                <Bar dataKey="amount" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          }
        </div>
        <div className="bg-white border border-slate-200 p-6">
          <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>By Method</h3>
          {r.per_method.length === 0 ? <p className="text-sm text-slate-500">—</p> :
            <ul className="space-y-2">
              {r.per_method.map((m) => (
                <li key={m.method} className="flex justify-between items-center border-b border-slate-100 pb-2 last:border-0">
                  <span className="text-sm font-medium uppercase tracking-wider text-slate-700">{m.method.replace('_', ' ')}</span>
                  <span className="font-bold">{fmtInr(m.amount)}</span>
                </li>
              ))}
            </ul>
          }
        </div>
      </div>
      <div className="bg-white border border-slate-200">
        <h3 className="text-lg font-semibold px-6 py-4 border-b border-slate-200" style={{fontFamily: 'Outfit, sans-serif'}}>Recent Payments</h3>
        {r.recent_payments.length === 0 ? <p className="px-6 py-8 text-center text-sm text-slate-500">No payments recorded this month.</p> :
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Date</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Invoice</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Customer</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Method</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-5 py-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              {r.recent_payments.map(p => (
                <tr key={p.payment_id} className="border-b border-slate-100">
                  <td className="px-5 py-3 text-sm">{p.paid_on}</td>
                  <td className="px-5 py-3 text-sm font-mono font-bold">{p.invoice_number}</td>
                  <td className="px-5 py-3 text-sm">{p.customer_name}</td>
                  <td className="px-5 py-3 text-sm uppercase tracking-wider text-slate-500">{p.method.replace('_', ' ')}</td>
                  <td className="px-5 py-3 text-sm font-bold text-right text-green-600">{fmtInr(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
};

const StatBox = ({ label, value, subtitle, color }) => (
  <div className="bg-white border border-slate-200 p-6">
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-3xl font-bold mt-2" style={{fontFamily: 'Outfit, sans-serif', color}}>{value}</div>
    {subtitle && <div className="text-xs text-slate-400 mt-1">{subtitle}</div>}
  </div>
);

/* -------- Customers Master Tab -------- */
const emptyCustomer = (user) => ({
  legal_name: "", display_name: "", customer_type: "retail",
  gstin: "", pan: "", email: "", phone: "",
  billing_address: { line1: "", city: "", state: "", pincode: "" },
  shipping_address: { line1: "", city: "", state: "", pincode: "" },
  contact_persons: [],
  credit_limit: 0, payment_terms: "Net 30", payment_days: 30,
  status: "active", tags: [], notes: "",
  owner_id: user?.user_id || "", owner_name: user?.name || "",
});

const CustomersTab = ({ reloadSignal, user, fmtInr, onEdit, onDelete }) => {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [marketer, setMarketer] = useState("");
  const [marketers, setMarketers] = useState([]);
  const canFilterMarketer = ["admin", "manager", "md"].includes((user?.role || "").toLowerCase());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [gotoPage, setGotoPage] = useState("");
  const reqIdRef = useRef(0);
  const firstLoadRef = useRef(true);
  const lastReloadRef = useRef(reloadSignal);
  const searchRef = useRef(null);
  const [pageHeaderH, setPageHeaderH] = useState(0);
  const [searchBarH, setSearchBarH] = useState(0);

  useLayoutEffect(() => {
    const el = document.querySelector(".page-header-sticky");
    if (!el) return;
    const upd = () => setPageHeaderH(el.offsetHeight);
    upd(); const ro = new ResizeObserver(upd); ro.observe(el);
    window.addEventListener("resize", upd);
    return () => { ro.disconnect(); window.removeEventListener("resize", upd); };
  }, []);
  useLayoutEffect(() => {
    const el = searchRef.current; if (!el) return;
    const upd = () => setSearchBarH(el.offsetHeight);
    upd(); const ro = new ResizeObserver(upd); ro.observe(el);
    return () => ro.disconnect();
  });

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search.trim()), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch, pageSize, typeFilter, statusFilter, marketer]);
  useEffect(() => {
    if (!canFilterMarketer) return;
    api.get("/users").then(r => setMarketers((r.data || []).filter(u => ["bde", "bdm"].includes((u.role || "").toLowerCase())))).catch(() => {});
  }, [canFilterMarketer]);

  const fetchCustomers = useCallback(async () => {
    const rid = ++reqIdRef.current;
    if (firstLoadRef.current) setLoading(true); else setRefreshing(true);
    setError(false);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize),
        requester_role: user?.role || "", requester_id: user?.user_id || "" });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (typeFilter !== "all") params.set("customer_type", typeFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (marketer) params.set("owner_id", marketer);
      const r = await api.get(`/customers-master?${params.toString()}`);
      if (rid !== reqIdRef.current) return;
      const d = r.data || {};
      setItems(d.items || []);
      setTotalCount(d.total_count || 0);
      setFilteredCount(d.filtered_count ?? d.total_count ?? 0);
      setTotalPages(d.total_pages || 1);
      if (d.page && d.page !== page) setPage(d.page);
    } catch {
      if (rid !== reqIdRef.current) return;
      setError(true); setItems([]);
    } finally {
      if (rid === reqIdRef.current) { setLoading(false); setRefreshing(false); firstLoadRef.current = false; }
    }
  }, [page, pageSize, debouncedSearch, typeFilter, statusFilter, marketer, user]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
  useEffect(() => {
    if (lastReloadRef.current === reloadSignal) return;
    lastReloadRef.current = reloadSignal; fetchCustomers();
  }, [reloadSignal, fetchCustomers]);

  const goTo = () => { const n = parseInt(gotoPage, 10); if (!Number.isNaN(n) && n >= 1 && n <= totalPages) setPage(n); setGotoPage(""); };
  const start = filteredCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, filteredCount);
  const nf = (n) => (n || 0).toLocaleString("en-IN");
  const hasFilters = !!(debouncedSearch || typeFilter !== "all" || statusFilter !== "all");

  return (
    <div data-testid="customers-list">
      <div ref={searchRef} className="sticky z-20 bg-slate-50 border border-slate-200 border-b-0 px-4 py-3 flex items-center gap-2 flex-wrap" style={{ top: pageHeaderH }}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total Customers</div>
          <div className="text-base font-bold text-slate-800 tabular-nums" data-testid="customers-total-count">{nf(totalCount)}</div>
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Search name, company, code, GSTIN, phone, email…" value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") setDebouncedSearch(search.trim()); }}
            data-testid="customer-search" className="w-full pl-9 pr-8 py-2 border border-slate-300 text-sm rounded outline-none focus:border-[#002FA7] bg-white" />
          {search && <button onClick={() => { setSearch(""); setDebouncedSearch(""); }} data-testid="customer-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"><X size={12} /></button>}
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="customer-type-filter" className="px-2.5 py-2 border border-slate-300 text-sm rounded bg-white">
          <option value="all">All types</option>
          {["retail", "dealer", "distributor", "enterprise", "service", "channel_partner"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="customer-status-filter" className="px-2.5 py-2 border border-slate-300 text-sm rounded bg-white">
          <option value="all">All statuses</option>
          {["prospect", "active", "blocked", "inactive", "blacklisted"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {canFilterMarketer && (
          <select value={marketer} onChange={e => setMarketer(e.target.value)} data-testid="customers-marketer-filter" className="px-2.5 py-2 border border-slate-300 text-sm rounded bg-white max-w-[160px]">
            <option value="">All Marketers</option>
            {marketers.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </select>
        )}
        <div className="text-xs text-slate-500 tabular-nums ml-auto" data-testid="customers-count">
          {filteredCount === 0 ? "0 results" : hasFilters ? <>{nf(filteredCount)} results from {nf(totalCount)}</> : <>Showing {nf(start)}–{nf(end)} of {nf(totalCount)}</>}
        </div>
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} data-testid="customers-page-size" className="px-2 py-1.5 border border-slate-300 rounded bg-white text-sm">
          {[50, 100, 250].map(s => <option key={s} value={s}>{s} / page</option>)}
        </select>
      </div>
      <div className="bg-white border border-slate-200 relative">
        {refreshing && <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#002FA7]/70 animate-pulse z-30" data-testid="customers-refreshing" />}
        <table className="w-full table-fixed" data-testid="customers-master-table">
          <colgroup>
            <col style={{ width: "52px" }} /><col style={{ width: "120px" }} /><col /><col style={{ width: "104px" }} />
            <col style={{ width: "150px" }} /><col style={{ width: "110px" }} /><col style={{ width: "120px" }} /><col style={{ width: "96px" }} /><col style={{ width: "96px" }} />
          </colgroup>
          <thead className="sticky z-10 bg-slate-50 border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.05)]" style={{ top: pageHeaderH + searchBarH }}>
            <tr className="text-[11px] uppercase font-bold text-slate-500">
              <th className="text-left px-4 py-3">S.No.</th>
              <th className="text-left px-4 py-3">Code</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">GSTIN</th>
              <th className="text-right px-4 py-3">Credit Limit</th>
              <th className="text-right px-4 py-3">Outstanding</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className={refreshing ? "opacity-50 transition-opacity" : "transition-opacity"}>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => <tr key={`sk-${i}`} className="border-b border-slate-100">{Array.from({ length: 9 }).map((__, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>)
            ) : error ? (
              <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">Couldn’t load customers. <button onClick={fetchCustomers} className="text-[#002FA7] underline" data-testid="customers-retry">Retry</button></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="9" className="px-4 py-12 text-center text-slate-400" data-testid="customers-empty">{hasFilters ? "No customers match your search." : "No customers"}</td></tr>
            ) : items.map((c, idx) => {
              const over = c.credit_limit > 0 && c.outstanding_balance > c.credit_limit;
              return (
                <tr key={c.customer_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.customer_id}`}>
                  <td className="px-4 py-3 text-slate-500 tabular-nums" data-testid={`customers-sl-${c.customer_id}`}>{(page - 1) * pageSize + idx + 1}</td>
                  <td className="px-4 py-3 font-mono text-xs truncate">{c.customer_code}</td>
                  <td className="px-4 py-3"><div className="font-semibold text-sm truncate">{c.legal_name}</div><div className="text-xs text-slate-500 truncate">{c.email || c.phone || "—"}</div></td>
                  <td className="px-4 py-3 text-xs capitalize">{c.customer_type?.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 text-xs font-mono truncate">{c.gstin || "—"}</td>
                  <td className="px-4 py-3 text-right text-sm tabular-nums">{fmtInr(c.credit_limit)}</td>
                  <td className={`px-4 py-3 text-right text-sm font-semibold tabular-nums ${over ? "text-red-600" : "text-slate-700"}`}>{fmtInr(c.outstanding_balance || 0)}{over && <Warning size={14} weight="fill" className="inline ml-1 -mt-1 text-red-600" />}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : c.status === "blocked" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{c.status}</span></td>
                  <td className="px-4 py-3"><div className="flex justify-end gap-1">
                    <button onClick={() => onEdit(c)} data-testid={`customer-edit-${c.customer_id}`} className="p-2 hover:bg-slate-100" title="Edit"><PencilSimple size={16} /></button>
                    <button onClick={() => onDelete(c)} data-testid={`customer-del-${c.customer_id}`} className="p-2 hover:bg-red-50 text-red-500" title="Delete"><Trash size={16} /></button>
                  </div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filteredCount > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap mt-3 text-sm" data-testid="customers-pagination">
          <div className="text-xs text-slate-500 tabular-nums">Showing {nf(start)}–{nf(end)} of {nf(filteredCount)} customers</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="customers-prev" className="px-2.5 py-1.5 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50">‹ Prev</button>
            {billingPageList(page, totalPages).map((n, i) => n === "…" ? <span key={`e${i}`} className="px-1.5 text-slate-400">…</span> : (
              <button key={n} onClick={() => setPage(n)} data-testid={`customers-page-${n}`} className={`min-w-[32px] px-2 py-1.5 border rounded ${n === page ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-300 hover:bg-slate-50"}`}>{n}</button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="customers-next" className="px-2.5 py-1.5 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50">Next ›</button>
            {totalPages > 7 && (
              <div className="flex items-center gap-1 ml-1">
                <input value={gotoPage} onChange={e => setGotoPage(e.target.value.replace(/\D/g, ""))} onKeyDown={e => e.key === "Enter" && goTo()} placeholder="Go" data-testid="customers-goto-input" className="w-14 px-2 py-1.5 border border-slate-300 rounded outline-none focus:border-[#002FA7]" />
                <button onClick={goTo} data-testid="customers-goto-btn" className="px-2 py-1.5 border border-slate-300 rounded hover:bg-slate-50">Go</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* -------- Customer Editor Modal -------- */
const CustomerEditorModal = ({ editor, setEditor, user, onSaved }) => {
  const [form, setForm] = useState(editor.data);
  const save = async () => {
    if (!form.legal_name) { toast.error("Legal name required"); return; }
    try {
      if (editor.mode === "create") {
        await api.post(`${API}/customers-master`, form);
        toast.success("Customer created");
      } else {
        await api.put(`${API}/customers-master/${form.customer_id}?requester_role=${user?.role || ""}`, form);
        toast.success("Customer updated");
      }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="customer-editor">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold">{editor.mode === "create" ? "New Customer" : `Edit ${form.customer_code || ""}`}</h2>
          <button onClick={() => setEditor(null)} className="text-slate-500 text-xl">×</button>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-3 text-sm">
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">Legal Name *</label>
            <input data-testid="customer-legal-name" value={form.legal_name} onChange={e => setForm({ ...form, legal_name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Display Name</label>
            <input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Customer Type</label>
            <select value={form.customer_type} onChange={e => setForm({ ...form, customer_type: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded capitalize">
              {["retail", "dealer", "distributor", "enterprise", "service", "channel_partner"].map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">GSTIN</label>
            <input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className="w-full px-3 py-2 border border-slate-300 rounded font-mono" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">PAN</label>
            <input value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} className="w-full px-3 py-2 border border-slate-300 rounded font-mono" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Phone</label>
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Credit Limit (₹)</label>
            <input type="number" data-testid="customer-credit-limit" value={form.credit_limit} onChange={e => setForm({ ...form, credit_limit: Number(e.target.value) })} className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Payment Terms</label>
            <input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} placeholder="Net 30 / Advance / On delivery" className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Payment Days</label>
            <input type="number" value={form.payment_days} onChange={e => setForm({ ...form, payment_days: Number(e.target.value) })} className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Status</label>
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded">
              {["prospect", "active", "blocked", "inactive", "blacklisted"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <p className="text-xs font-bold text-slate-600 mb-1">Billing Address</p>
            <div className="grid md:grid-cols-4 gap-2">
              <input placeholder="Line 1" value={form.billing_address?.line1 || ""} onChange={e => setForm({ ...form, billing_address: { ...form.billing_address, line1: e.target.value } })} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded" />
              <input placeholder="City" value={form.billing_address?.city || ""} onChange={e => setForm({ ...form, billing_address: { ...form.billing_address, city: e.target.value } })} className="px-3 py-2 border border-slate-300 rounded" />
              <input placeholder="State" value={form.billing_address?.state || ""} onChange={e => setForm({ ...form, billing_address: { ...form.billing_address, state: e.target.value } })} className="px-3 py-2 border border-slate-300 rounded" />
              <input placeholder="Pincode" value={form.billing_address?.pincode || ""} onChange={e => setForm({ ...form, billing_address: { ...form.billing_address, pincode: e.target.value } })} className="px-3 py-2 border border-slate-300 rounded" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-bold text-slate-600 mb-1">Notes</label>
            <textarea rows="2" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={() => setEditor(null)} className="px-4 py-2 border border-slate-300 rounded text-sm">Cancel</button>
          <button data-testid="customer-save-btn" onClick={save} className="px-4 py-2 bg-[#002FA7] text-white rounded text-sm">Save</button>
        </div>
      </div>
    </div>
  );
};

/* -------- Aging Report Tab -------- */
const AgingReport = ({ data, fmtInr }) => {
  if (!data) return null;
  const { rows, totals, as_of } = data;
  return (
    <div data-testid="aging-report">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        <StatBox label="Current" value={fmtInr(totals.current)} color="#10b981" />
        <StatBox label="0–30 days" value={fmtInr(totals.d0_30)} color="#f59e0b" />
        <StatBox label="31–60 days" value={fmtInr(totals.d31_60)} color="#f97316" />
        <StatBox label="61–90 days" value={fmtInr(totals.d61_90)} color="#ef4444" />
        <StatBox label="90+ days" value={fmtInr(totals.d90_plus)} color="#dc2626" />
        <StatBox label="Total Outstanding" value={fmtInr(totals.total)} color="#002FA7" subtitle={`${totals.customer_count} customers · as of ${as_of}`} />
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full min-w-[900px]" data-testid="aging-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left text-xs uppercase font-bold text-slate-500 px-4 py-3">Customer</th>
              <th className="text-right text-xs uppercase font-bold text-slate-500 px-4 py-3"># Inv</th>
              <th className="text-right text-xs uppercase font-bold text-slate-500 px-4 py-3">Current</th>
              <th className="text-right text-xs uppercase font-bold text-slate-500 px-4 py-3">0–30</th>
              <th className="text-right text-xs uppercase font-bold text-slate-500 px-4 py-3">31–60</th>
              <th className="text-right text-xs uppercase font-bold text-slate-500 px-4 py-3">61–90</th>
              <th className="text-right text-xs uppercase font-bold text-slate-500 px-4 py-3">90+</th>
              <th className="text-right text-xs uppercase font-bold text-slate-500 px-4 py-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400">No outstanding receivables</td></tr>}
            {rows.map(r => (
              <tr key={r.customer_id} className="border-b border-slate-100">
                <td className="px-4 py-3 font-semibold text-sm">{r.customer_name}</td>
                <td className="px-4 py-3 text-right text-sm">{r.invoice_count}</td>
                <td className="px-4 py-3 text-right text-sm">{r.current > 0 ? fmtInr(r.current) : "—"}</td>
                <td className="px-4 py-3 text-right text-sm text-amber-700">{r.d0_30 > 0 ? fmtInr(r.d0_30) : "—"}</td>
                <td className="px-4 py-3 text-right text-sm text-orange-700">{r.d31_60 > 0 ? fmtInr(r.d31_60) : "—"}</td>
                <td className="px-4 py-3 text-right text-sm text-red-600">{r.d61_90 > 0 ? fmtInr(r.d61_90) : "—"}</td>
                <td className="px-4 py-3 text-right text-sm text-red-700 font-bold">{r.d90_plus > 0 ? fmtInr(r.d90_plus) : "—"}</td>
                <td className="px-4 py-3 text-right text-sm font-bold text-[#002FA7]">{fmtInr(r.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* -------- Sales Orders Tab -------- */
const SalesOrdersTab = ({ reloadSignal, fmtInr, user, onChanged }) => {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [marketer, setMarketer] = useState("");
  const [marketers, setMarketers] = useState([]);
  const canFilterMarketer = ["admin", "manager", "md"].includes((user?.role || "").toLowerCase());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [gotoPage, setGotoPage] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [pQuery, setPQuery] = useState("");
  const [pDebounced, setPDebounced] = useState("");
  const [pItems, setPItems] = useState([]);
  const [pCount, setPCount] = useState(0);
  const [pLoading, setPLoading] = useState(false);
  const pReqRef = useRef(0);
  const reqIdRef = useRef(0);
  const firstLoadRef = useRef(true);
  const lastReloadRef = useRef(reloadSignal);
  const searchRef = useRef(null);
  const [pageHeaderH, setPageHeaderH] = useState(0);
  const [searchBarH, setSearchBarH] = useState(0);

  useLayoutEffect(() => {
    const el = document.querySelector(".page-header-sticky");
    if (!el) return;
    const upd = () => setPageHeaderH(el.offsetHeight);
    upd(); const ro = new ResizeObserver(upd); ro.observe(el);
    window.addEventListener("resize", upd);
    return () => { ro.disconnect(); window.removeEventListener("resize", upd); };
  }, []);
  useLayoutEffect(() => {
    const el = searchRef.current; if (!el) return;
    const upd = () => setSearchBarH(el.offsetHeight);
    upd(); const ro = new ResizeObserver(upd); ro.observe(el);
    return () => ro.disconnect();
  });

  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search.trim()), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch, pageSize, statusFilter, marketer]);
  useEffect(() => {
    if (!canFilterMarketer) return;
    api.get("/users").then(r => setMarketers((r.data || []).filter(u => ["bde", "bdm"].includes((u.role || "").toLowerCase())))).catch(() => {});
  }, [canFilterMarketer]);

  const fetchOrders = useCallback(async () => {
    const rid = ++reqIdRef.current;
    if (firstLoadRef.current) setLoading(true); else setRefreshing(true);
    setError(false);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize),
        requester_role: user?.role || "", requester_id: user?.user_id || "", requester_name: user?.name || "" });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (marketer) params.set("marketer_id", marketer);
      const r = await api.get(`/sales-orders?${params.toString()}`);
      if (rid !== reqIdRef.current) return;
      const d = r.data || {};
      setItems(d.items || []);
      setTotalCount(d.total_count || 0);
      setFilteredCount(d.filtered_count ?? d.total_count ?? 0);
      setTotalPages(d.total_pages || 1);
      if (d.page && d.page !== page) setPage(d.page);
    } catch {
      if (rid !== reqIdRef.current) return;
      setError(true); setItems([]);
    } finally {
      if (rid === reqIdRef.current) { setLoading(false); setRefreshing(false); firstLoadRef.current = false; }
    }
  }, [page, pageSize, debouncedSearch, statusFilter, marketer, user]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => {
    if (lastReloadRef.current === reloadSignal) return;
    lastReloadRef.current = reloadSignal; fetchOrders();
  }, [reloadSignal, fetchOrders]);

  useEffect(() => { if (!showCreate) return; const t = setTimeout(() => setPDebounced(pQuery.trim()), 350); return () => clearTimeout(t); }, [pQuery, showCreate]);
  useEffect(() => {
    if (!showCreate) { setPQuery(""); setPDebounced(""); setPItems([]); setPCount(0); return; }
    const rid = ++pReqRef.current;
    setPLoading(true);
    const params = new URLSearchParams({ page: "1", page_size: "25" });
    if (pDebounced) params.set("search", pDebounced);
    api.get(`/sales-orders/eligible-quotations?${params.toString()}`).then(r => {
      if (rid !== pReqRef.current) return;
      const d = r.data || {};
      setPItems(d.items || []); setPCount(d.filtered_count || 0);
    }).catch(() => { if (rid === pReqRef.current) { setPItems([]); setPCount(0); } })
      .finally(() => { if (rid === pReqRef.current) setPLoading(false); });
  }, [showCreate, pDebounced]);

  const createFromQuote = async (qid, eta) => {
    try {
      await api.post(`${API}/sales-orders?requester_role=${user?.role || ""}&requester_user_id=${user?.user_id || ""}`,
        { quote_doc_id: qid, expected_delivery_date: eta, created_by: user.user_id, created_by_name: user.name });
      toast.success("Sales order created");
      setShowCreate(false); onChanged ? onChanged() : fetchOrders();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };

  const fulfill = async (so) => {
    const amt = window.prompt(`Fulfill amount (total ₹${so.total}, already ₹${so.fulfilled_amount || 0}):`, String(so.total - (so.fulfilled_amount || 0)));
    if (!amt) return;
    try {
      await api.put(`${API}/sales-orders/${so.so_id}/fulfill?requester_role=${user?.role || ""}`,
        { amount: Number(amt), by_user_id: user.user_id, by_user_name: user.name, by_role: user.role });
      toast.success("Fulfillment recorded"); onChanged ? onChanged() : fetchOrders();
    } catch { toast.error("Fulfill failed"); }
  };

  const goTo = () => { const n = parseInt(gotoPage, 10); if (!Number.isNaN(n) && n >= 1 && n <= totalPages) setPage(n); setGotoPage(""); };
  const start = filteredCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, filteredCount);
  const nf = (n) => (n || 0).toLocaleString("en-IN");
  const hasFilters = !!(debouncedSearch || statusFilter !== "all" || marketer);
  const canManage = ["admin", "manager", "bdm"].includes((user?.role || "").toLowerCase());

  return (
    <div data-testid="sales-orders-list">
      <div ref={searchRef} className="sticky z-20 bg-slate-50 border border-slate-200 border-b-0 px-4 py-3 flex items-center gap-2 flex-wrap" style={{ top: pageHeaderH }}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total Sales Orders</div>
          <div className="text-base font-bold text-slate-800 tabular-nums" data-testid="sales-orders-total-count">{nf(totalCount)}</div>
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Search SO number, customer, GST, phone, email, marketer…" value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") setDebouncedSearch(search.trim()); }}
            data-testid="sales_orders-search" className="w-full pl-9 pr-8 py-2 border border-slate-300 text-sm rounded outline-none focus:border-[#002FA7] bg-white" />
          {search && <button onClick={() => { setSearch(""); setDebouncedSearch(""); }} data-testid="sales_orders-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"><X size={12} /></button>}
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="sales_orders-status-filter" className="px-2.5 py-2 border border-slate-300 text-sm rounded bg-white">
          <option value="all">All statuses</option>
          {["open", "partial", "fulfilled", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {canFilterMarketer && (
          <select value={marketer} onChange={e => setMarketer(e.target.value)} data-testid="sales_orders-marketer-filter" className="px-2.5 py-2 border border-slate-300 text-sm rounded bg-white max-w-[160px]">
            <option value="">All Marketers</option>
            {marketers.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </select>
        )}
        <div className="text-xs text-slate-500 tabular-nums ml-auto" data-testid="sales_orders-count">
          {filteredCount === 0 ? "0 results" : hasFilters ? <>{nf(filteredCount)} results from {nf(totalCount)}</> : <>Showing {nf(start)}–{nf(end)} of {nf(totalCount)}</>}
        </div>
        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} data-testid="sales_orders-page-size" className="px-2 py-1.5 border border-slate-300 rounded bg-white text-sm">
          {[50, 100, 250].map(s => <option key={s} value={s}>{s} / page</option>)}
        </select>
        {["admin", "manager", "bdm", "bde"].includes((user?.role || "").toLowerCase()) && (
          <button data-testid="so-create-btn" onClick={() => setShowCreate(true)}
            className="px-3 py-2 bg-[#002FA7] text-white text-sm rounded flex items-center gap-1">
            <Plus size={14} weight="bold" /> New SO
          </button>
        )}
      </div>
      <div className="bg-white border border-slate-200 relative">
        {refreshing && <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#002FA7]/70 animate-pulse z-30" data-testid="sales-orders-refreshing" />}
        <table className="w-full table-fixed" data-testid="sales-orders-table">
          <colgroup>
            <col style={{ width: "52px" }} /><col style={{ width: "130px" }} /><col /><col style={{ width: "130px" }} />
            <col style={{ width: "120px" }} /><col style={{ width: "120px" }} /><col style={{ width: "110px" }} /><col style={{ width: "100px" }} /><col style={{ width: "90px" }} />
          </colgroup>
          <thead className="sticky z-10 bg-slate-50 border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.05)]" style={{ top: pageHeaderH + searchBarH }}>
            <tr className="text-[11px] uppercase font-bold text-slate-500">
              <th className="text-left px-4 py-3">S.No.</th>
              <th className="text-left px-4 py-3">SO Number</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Marketer</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Fulfilled</th>
              <th className="text-left px-4 py-3">ETA</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className={refreshing ? "opacity-50 transition-opacity" : "transition-opacity"}>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => <tr key={`sk-${i}`} className="border-b border-slate-100">{Array.from({ length: 9 }).map((__, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>)
            ) : error ? (
              <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">Couldn’t load sales orders. <button onClick={fetchOrders} className="text-[#002FA7] underline" data-testid="sales-orders-retry">Retry</button></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="9" className="px-4 py-12 text-center text-slate-400" data-testid="sales-orders-empty">{hasFilters ? "No sales orders match your search." : "No sales orders yet"}</td></tr>
            ) : items.map((so, idx) => (
              <tr key={so.so_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`so-row-${so.so_id}`}>
                <td className="px-4 py-3 text-slate-500 tabular-nums" data-testid={`sales_orders-sl-${so.so_id}`}>{(page - 1) * pageSize + idx + 1}</td>
                <td className="px-4 py-3 font-mono text-sm truncate">{so.so_number}</td>
                <td className="px-4 py-3 text-sm truncate">{so.customer_name}</td>
                <td className="px-4 py-3 text-xs text-slate-600 truncate">{so.business_owner_name || "Unassigned"}</td>
                <td className="px-4 py-3 text-right text-sm font-bold text-[#002FA7] tabular-nums">{fmtInr(so.total)}</td>
                <td className="px-4 py-3 text-right text-sm tabular-nums">{fmtInr(so.fulfilled_amount || 0)}</td>
                <td className="px-4 py-3 text-xs">{so.expected_delivery_date || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${so.status === "fulfilled" ? "bg-emerald-100 text-emerald-700" : so.status === "partial" ? "bg-amber-100 text-amber-700" : so.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                    {so.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {so.status !== "fulfilled" && so.status !== "cancelled" && canManage && (
                    <button onClick={() => fulfill(so)} data-testid={`so-fulfill-${so.so_id}`} className="px-2 py-1 text-xs bg-emerald-600 text-white rounded">Fulfill</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredCount > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap mt-3 text-sm" data-testid="sales-orders-pagination">
          <div className="text-xs text-slate-500 tabular-nums">Showing {nf(start)}–{nf(end)} of {nf(filteredCount)} sales orders</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="sales-orders-prev" className="px-2.5 py-1.5 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50">‹ Prev</button>
            {billingPageList(page, totalPages).map((n, i) => n === "…" ? <span key={`e${i}`} className="px-1.5 text-slate-400">…</span> : (
              <button key={n} onClick={() => setPage(n)} data-testid={`sales-orders-page-${n}`} className={`min-w-[32px] px-2 py-1.5 border rounded ${n === page ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-300 hover:bg-slate-50"}`}>{n}</button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="sales-orders-next" className="px-2.5 py-1.5 border border-slate-300 rounded disabled:opacity-40 hover:bg-slate-50">Next ›</button>
            {totalPages > 7 && (
              <div className="flex items-center gap-1 ml-1">
                <input value={gotoPage} onChange={e => setGotoPage(e.target.value.replace(/\D/g, ""))} onKeyDown={e => e.key === "Enter" && goTo()} placeholder="Go" data-testid="sales-orders-goto-input" className="w-14 px-2 py-1.5 border border-slate-300 rounded outline-none focus:border-[#002FA7]" />
                <button onClick={goTo} data-testid="sales-orders-goto-btn" className="px-2 py-1.5 border border-slate-300 rounded hover:bg-slate-50">Go</button>
              </div>
            )}
          </div>
        </div>
      )}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-lg w-full max-w-xl p-5" data-testid="so-create-modal">
            <h2 className="text-lg font-bold mb-1">New Sales Order from Approved Quotation</h2>
            <p className="text-xs text-slate-500 mb-3">Search any eligible approved quotation — newest shown first.</p>
            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input autoFocus value={pQuery} onChange={e => setPQuery(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") setPDebounced(pQuery.trim()); }}
                data-testid="so-picker-search" placeholder="Search quotation number, customer, GST, phone, marketer…"
                className="w-full pl-9 pr-8 py-2 border border-slate-300 rounded text-sm outline-none focus:border-[#002FA7] bg-white" />
              {pQuery && <button onClick={() => { setPQuery(""); setPDebounced(""); }} data-testid="so-picker-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"><X size={12} /></button>}
            </div>
            <div className="text-[11px] text-slate-500 mb-2" data-testid="so-picker-count">
              {pLoading ? "Searching…" : (pDebounced ? `${(pCount || 0).toLocaleString("en-IN")} matching` : `${(pCount || 0).toLocaleString("en-IN")} eligible`)}{pCount > pItems.length ? ` · showing ${pItems.length}` : ""}
            </div>
            <div className="max-h-[46vh] overflow-y-auto border border-slate-100 rounded" data-testid="so-picker-list">
              {pLoading ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400">Loading…</div>
              ) : pItems.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="so-picker-empty">{pDebounced ? "No matching approved quotations." : "No approved quotations available."}</div>
              ) : pItems.map(qq => (
                <div key={qq.doc_id} className="border-b border-slate-100 last:border-0 py-3 px-3 flex items-center justify-between hover:bg-slate-50">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{qq.number} — {qq.customer_name || qq.customer_company || "—"}</p>
                    <p className="text-xs text-slate-500 truncate">{fmtInr(qq.total)} · {qq.created_by_name || qq.salesperson || qq.assigned_to || "Unassigned"}</p>
                  </div>
                  <button data-testid={`so-from-${qq.number}`} onClick={() => {
                    const eta = window.prompt("Expected delivery date (YYYY-MM-DD):", "");
                    if (eta !== null) createFromQuote(qq.doc_id, eta);
                  }} className="ml-3 shrink-0 px-3 py-1 bg-[#002FA7] text-white text-xs rounded">Create SO</button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setShowCreate(false)} data-testid="so-picker-close" className="px-4 py-2 border border-slate-300 rounded text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* -------- Price Books Tab -------- */
const PriceBooksTab = ({ books, fmtInr, user, onEdit, onDelete }) => (
  <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="price-books-table">
    <table className="w-full min-w-[700px]">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          <th className="text-left text-xs uppercase font-bold text-slate-500 px-4 py-3">Name</th>
          <th className="text-left text-xs uppercase font-bold text-slate-500 px-4 py-3">Customer Type</th>
          <th className="text-right text-xs uppercase font-bold text-slate-500 px-4 py-3">Items</th>
          <th className="text-left text-xs uppercase font-bold text-slate-500 px-4 py-3">Valid</th>
          <th className="text-left text-xs uppercase font-bold text-slate-500 px-4 py-3">Active</th>
          <th className="text-right text-xs uppercase font-bold text-slate-500 px-4 py-3">Actions</th>
        </tr>
      </thead>
      <tbody>
        {books.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400">No price books configured</td></tr>}
        {books.map(pb => (
          <tr key={pb.price_book_id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="px-4 py-3 font-semibold text-sm">{pb.name}</td>
            <td className="px-4 py-3 text-xs capitalize">{pb.customer_type?.replace(/_/g, " ")}</td>
            <td className="px-4 py-3 text-right text-sm">{(pb.items || []).length}</td>
            <td className="px-4 py-3 text-xs text-slate-500">{(pb.valid_from || "—")} → {(pb.valid_to || "—")}</td>
            <td className="px-4 py-3 text-xs">{pb.active ? <span className="text-emerald-700">✓</span> : <span className="text-slate-400">—</span>}</td>
            <td className="px-4 py-3 text-right">
              {["admin", "manager"].includes(user?.role) && (
                <>
                  <button onClick={() => onEdit(pb)} className="p-2 hover:bg-slate-100"><PencilSimple size={16} /></button>
                  <button onClick={() => onDelete(pb)} className="p-2 hover:bg-red-50 text-red-500"><Trash size={16} /></button>
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* -------- Price Book Editor -------- */
const PriceBookEditorModal = ({ editor, setEditor, user, onSaved }) => {
  const [form, setForm] = useState(editor.data);
  const addItem = () => setForm(f => ({ ...f, items: [...(f.items || []), { row_id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6), item_code: "", name: "", unit_price: 0, volume_slabs: [] }] }));
  const updItem = (i, patch) => setForm(f => { const items = [...f.items]; items[i] = { ...items[i], ...patch }; return { ...f, items }; });
  const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const save = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      if (editor.mode === "create") {
        await axios.post(`${API}/price-books?requester_role=${user.role}`, form);
        toast.success("Price book created");
      } else {
        await axios.put(`${API}/price-books/${form.price_book_id}?requester_role=${user.role}`, form);
        toast.success("Price book updated");
      }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5" data-testid="price-book-editor">
        <h2 className="text-lg font-bold mb-4">{editor.mode === "create" ? "New Price Book" : `Edit ${form.name}`}</h2>
        <div className="grid md:grid-cols-2 gap-3 mb-4 text-sm">
          <input data-testid="pb-name" placeholder="Name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border border-slate-300 rounded" />
          <select value={form.customer_type} onChange={e => setForm({ ...form, customer_type: e.target.value })} className="px-3 py-2 border border-slate-300 rounded capitalize">
            {["all", "retail", "dealer", "distributor", "enterprise", "service", "channel_partner"].map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <input type="date" placeholder="Valid from" value={form.valid_from || ""} onChange={e => setForm({ ...form, valid_from: e.target.value })} className="px-3 py-2 border border-slate-300 rounded" />
          <input type="date" placeholder="Valid to" value={form.valid_to || ""} onChange={e => setForm({ ...form, valid_to: e.target.value })} className="px-3 py-2 border border-slate-300 rounded" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} /> Active</label>
        </div>
        <div className="border-t border-slate-200 pt-3">
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-sm">Items</p>
            <button type="button" onClick={addItem} className="text-xs px-2 py-1 bg-[#002FA7] text-white rounded">+ Add Item</button>
          </div>
          <div className="space-y-2">
            {(form.items || []).map((it, i) => (
              <div key={it.row_id || `pb-item-${i}`} className="border border-slate-200 rounded p-2 grid grid-cols-4 gap-2 items-center">
                <input placeholder="Item code" value={it.item_code} onChange={e => updItem(i, { item_code: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs" />
                <input placeholder="Name" value={it.name} onChange={e => updItem(i, { name: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs" />
                <input type="number" placeholder="Unit price ₹" value={it.unit_price} onChange={e => updItem(i, { unit_price: Number(e.target.value) })} className="px-2 py-1 border border-slate-300 rounded text-xs" />
                <button onClick={() => rmItem(i)} className="text-red-600 text-xs hover:underline">Remove</button>
              </div>
            ))}
            {(form.items || []).length === 0 && <p className="text-xs text-slate-500">No items yet.</p>}
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setEditor(null)} className="px-4 py-2 border border-slate-300 rounded text-sm">Cancel</button>
          <button data-testid="pb-save-btn" onClick={save} className="px-4 py-2 bg-[#002FA7] text-white rounded text-sm">Save</button>
        </div>
      </div>
    </div>
  );
};

/* -------- Advanced Reports Tab -------- */
const ADV_REPORT_OPTS = [
  { key: "quotation_aging", label: "Quotation Aging" },
  { key: "win_loss", label: "Win / Loss Analysis" },
  { key: "bdm_turnaround", label: "BDM Approval Turnaround" },
  { key: "discount_deviation", label: "Discount Deviation" },
];

const AdvancedReportsTab = ({ state, setState, fmtInr }) => {
  const { type, data } = state;
  return (
    <div data-testid="advanced-reports">
      <div className="flex items-center gap-2 mb-4">
        <select data-testid="adv-report-type" value={type} onChange={e => setState(s => ({ ...s, type: e.target.value, data: null }))}
          className="px-3 py-2 border border-slate-300 rounded text-sm">
          {ADV_REPORT_OPTS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>
      {!data && <p className="text-slate-500">Loading…</p>}
      {data && type === "quotation_aging" && (
        <>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <StatBox label="0–7 days" value={data.buckets.d0_7} color="#10b981" />
            <StatBox label="8–30 days" value={data.buckets.d8_30} color="#f59e0b" />
            <StatBox label="31–60 days" value={data.buckets.d31_60} color="#f97316" />
            <StatBox label="60+ days" value={data.buckets.d60_plus} color="#ef4444" />
          </div>
          <SimpleTable rows={data.rows} cols={["number", "customer", "total", "age_days", "approval_status", "bucket"]} fmtInr={fmtInr} />
        </>
      )}
      {data && type === "win_loss" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <StatBox label="Total Quotes" value={data.total_quotes} color="#002FA7" />
            <StatBox label="Approved" value={data.approved} color="#10b981" />
            <StatBox label="Rejected" value={data.rejected} color="#ef4444" />
            <StatBox label="Converted" value={data.converted} color="#8b5cf6" />
            <StatBox label="Win Rate" value={`${data.win_rate}%`} subtitle={`${fmtInr(data.won_revenue)}`} color="#059669" />
          </div>
          <SimpleTable rows={data.top_winners} cols={["customer", "revenue"]} fmtInr={fmtInr} />
        </>
      )}
      {data && type === "bdm_turnaround" && <SimpleTable rows={data.rows} cols={["user_name", "approvals", "avg_turnaround_hours"]} fmtInr={fmtInr} />}
      {data && type === "discount_deviation" && (
        <>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <StatBox label="Low (≤5%)" value={data.summary.count_low} color="#10b981" />
            <StatBox label="Medium (6–15%)" value={data.summary.count_medium} color="#f59e0b" />
            <StatBox label="High (>15%)" value={data.summary.count_high} color="#ef4444" />
            <StatBox label="Avg Discount" value={`${data.summary.avg_discount}%`} color="#002FA7" />
          </div>
          <SimpleTable rows={data.rows} cols={["number", "customer", "discount_pct", "total", "approval_status", "created_by", "severity"]} fmtInr={fmtInr} />
        </>
      )}
    </div>
  );
};

const SimpleTable = ({ rows, cols, fmtInr }) => (
  <div className="bg-white border border-slate-200 overflow-x-auto">
    <table className="w-full min-w-[700px] text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>{cols.map(c => <th key={c} className="text-left text-xs uppercase font-bold text-slate-500 px-4 py-3">{c.replace(/_/g, " ")}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-6 text-center text-slate-400">No data</td></tr>}
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-100">
            {cols.map(c => (
              <td key={c} className="px-4 py-2 text-xs">
                {typeof r[c] === "number" && (c.includes("revenue") || c === "total") ? fmtInr(r[c]) : typeof r[c] === "number" ? r[c].toLocaleString("en-IN") : r[c]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default BillingCenter;
